// ============================================
// CHÁMOMILE - Premium Oatmilk Tea Online Ordering
// Version 3.4 - Bug Fixes & Performance Enhancements
// ============================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  staggerContainer,
  cardVariants,
  fadeInUp,
  popIn,
  successVariants,
  createConfetti,
  createRipple,
  prefersReducedMotion
} from './utils/animations';
import ReviewPage from './pages/ReviewPage';
import './styles/app.css';


// Firebase imports
import { collection, getDocs, updateDoc, doc, getDoc, query, orderBy, limit, onSnapshot, where } from 'firebase/firestore';
import { db, isFirebaseConfigured } from './config/firebase';

import { Clock, CheckCircle2, Coffee, Package, XCircle, Download } from 'lucide-react';

// Extracted modules
import { colors } from './constants/colors';
import { Icons } from './constants/icons';
import { styles } from './constants/styles';
import { CookieManager, BrowserUtils, ImageUtils, ImagePreloader, generateOrderId } from './utils/browser';
import { uploadReceiptToFirebase, saveOrderToFirebase } from './utils/firebaseUpload';
import { missingCheckoutFields, normalizeMyPhone, isValidMyMobile } from './utils/checkout';

// Import order service for sequential order numbers
import orderService from './services/orderService';

// Import review service
import {
  submitReview,
  getProductReviews,
  getProductRating,
  getAllProductRatings,
  formatReviewDate,
  createReviewDiscountCode,
  markDiscountCodeAsUsed
} from './services/reviewService';

// Import Pinterest-style review components
import { PinterestMasonryGrid } from './components/ProductReviewsDisplay';
import { PrivateEventBanner, EventInquiryModal } from './components/EventInquiryModal';
import CategoryArcBar from './components/CategoryArcBar';
import ProductCard from './components/ProductCard';
import StarRating from './components/StarRating';

// Import product service for dynamic Firestore loading
import { fetchOnlineProducts, fetchCategoriesAndIcons, fetchProductTags } from './services/productService';

// Fallback product data (used when Firestore is unavailable)
import { products as fallbackProducts } from './data/products';

// Auth imports
import AuthModal from './components/AuthModal';
import { onAuthChange, logOut, getCurrentUser } from './config/firebase';

// Loyalty imports
import { getMemberByAuthUid, getMemberByEmail, getMemberByPhone, getMemberTier, getNextReward, earnPoints, setGlobalRewards, REWARDS_UI_ENABLED } from './services/loyaltyService';
import MemberDashboard from './components/MemberDashboard';

const TIMES = ['10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM'];
// The bank shows a different name depending on how the customer pays: the
// DuitNow QR displays the QR's name, an account-number transfer the account holder.
const BANK = { name: 'GARDEN BREWERY', qrName: 'CHAMOMILE', number: '5601 0270 7392', bank: 'Maybank' };
const DUITNOW_QR_URL = '/duitnow-qr.png';
const copyBtnStyle = { minWidth: '72px', minHeight: '44px', padding: '0 14px', borderRadius: '999px', border: `1.5px solid ${colors.gold400}`, background: colors.surface, color: colors.gold700, fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 };
const linkBtnStyle = { background: 'none', border: 'none', color: colors.gold700, textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' };
const LOCATION = { name: 'Forest Hill Residences', address: 'Kota Kinabalu, Sabah', hours: '10:00 AM - 10:00 PM daily' };

// Design system constants, Icons, styles, and utilities
// are imported from extracted modules above (see imports).

const MiniOrderTracker = ({ order }) => {
  if (!order) return null;

  const workflow = [
    { id: 'pending', label: 'Placed', icon: Clock },
    { id: 'confirmed', label: 'Confirmed', icon: CheckCircle2 },
    { id: 'preparing', label: 'Preparing', icon: Coffee },
    { id: 'ready', label: 'Ready', icon: Package },
    { id: 'completed', label: 'Completed', icon: CheckCircle2 },
  ];

  if (order.status === 'cancelled') {
    return (
      <div style={{ padding: '10px 16px', borderBottom: `1px solid ${colors.stone100}`, display: 'flex', alignItems: 'center', gap: '8px', color: colors.error }}>
        <XCircle size={14} />
        <span style={{ fontSize: '12px', fontWeight: 600 }}>Order #{order.orderNumber || order.orderId?.slice(-6)} Cancelled</span>
      </div>
    );
  }

  // Treat 'payment_uploaded' as 'pending' visually
  const normalizedStatus = order.status === 'payment_uploaded' ? 'pending' : (order.status || 'pending');
  const currentIndex = workflow.findIndex(s => s.id === normalizedStatus);
  const activeIndex = currentIndex === -1 ? 0 : currentIndex;

  return (
    <div style={{ padding: '16px 16px 20px', borderBottom: `1px solid ${colors.stone100}`, background: colors.stone50 }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: colors.stone800, marginBottom: '16px' }}>
        Track Order #{order.orderNumber || order.orderId?.slice(-6)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
        {/* Progress Line */}
        <div style={{ position: 'absolute', top: '9px', left: '10%', right: '10%', height: '2px', background: colors.stone200, zIndex: 0, borderRadius: '1px' }} />
        <div style={{ position: 'absolute', top: '9px', left: '10%', right: '10%', height: '2px', background: colors.gold400, zIndex: 0, width: `${(activeIndex / (workflow.length - 1)) * 100}%`, transition: 'width 0.4s ease', borderRadius: '1px' }} />

        {/* Steps */}
        {workflow.map((step, idx) => {
          const isCompleted = idx <= activeIndex;
          const isActive = idx === activeIndex;
          const Icon = step.icon;
          return (
            <div key={step.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1, flex: 1 }}>
              <div style={{ 
                width: '20px', height: '20px', borderRadius: '50%', 
                background: isCompleted ? colors.gold500 : '#fff',
                border: `2px solid ${isCompleted ? colors.gold500 : colors.stone300}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: isCompleted ? '#fff' : colors.stone400,
                boxShadow: isActive ? `0 0 0 3px ${colors.gold100}` : 'none',
                transition: 'all 0.3s ease'
              }}>
                <Icon size={12} />
              </div>
              <span style={{ 
                fontSize: '10px', marginTop: '6px', fontWeight: isActive ? 700 : (isCompleted ? 600 : 500),
                color: isActive ? colors.gold700 : (isCompleted ? colors.stone800 : colors.stone400),
                textAlign: 'center', lineHeight: 1.2
              }}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// COMPONENTS
// ============================================

const InteractiveStarRating = ({ rating, onRatingChange, size = 28 }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={() => onRatingChange(star)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: `${size}px`,
            color: star <= (hoverRating || rating) ? colors.gold400 : colors.stone300,
            transition: 'all 0.15s ease',
            transform: star <= (hoverRating || rating) ? 'scale(1.15)' : 'scale(1)',
            padding: '4px',
          }}
        >
          {star <= (hoverRating || rating) ? '★' : '☆'}
        </button>
      ))}
    </div>
  );
};

// Standard image loading is used for 100% compatibility with Android browsers
// CSS-based placeholders are preferred over React state-managed loaders in these environments




const ReviewCard = ({ review }) => {
  const [showFullImage, setShowFullImage] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <>
      <div style={{
        background: '#fff',
        border: `1px solid ${colors.stone100}`,
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px', color: colors.stone800 }}>{review.customerName}</span>
              {review.isVerifiedPurchase && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 8px',
                  background: colors.success + '15',
                  color: colors.success,
                  fontSize: '10px',
                  fontWeight: 600,
                  borderRadius: '999px',
                }}>
                  <Icons.BadgeCheck size={12} /> Verified
                </span>
              )}
            </div>
            <StarRating rating={review.rating} showCount={false} size={12} />
          </div>
          <span style={{ fontSize: '11px', color: colors.stone400 }}>
            {formatReviewDate(review.createdAt)}
          </span>
        </div>
        {review.title && (
          <h4 style={{ fontSize: '13px', fontWeight: 600, color: colors.stone700, marginBottom: '4px' }}>
            {review.title}
          </h4>
        )}
        <p style={{ fontSize: '13px', color: colors.stone600, lineHeight: 1.6, margin: 0 }}>
          {review.comment}
        </p>
        {review.imageUrl && !imageError && (
          <div
            style={{
              marginTop: '12px',
              borderRadius: '10px',
              overflow: 'hidden',
              cursor: 'pointer',
              border: `1px solid ${colors.stone200}`,
            }}
            onClick={() => setShowFullImage(true)}
          >
            <img
              src={review.imageUrl}
              alt="Customer review"
              referrerPolicy="no-referrer"
              style={{
                width: '100%',
                maxHeight: '200px',
                objectFit: 'cover',
              }}
              onError={() => setImageError(true)}
            />

          </div>
        )}
        {review.response && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: colors.gold50,
            borderRadius: '8px',
            borderLeft: `3px solid ${colors.gold400}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: colors.gold600 }}>Chámomile Response</span>
            </div>
            <p style={{ fontSize: '12px', color: colors.stone600, margin: 0 }}>{review.response}</p>
          </div>
        )}
      </div>

      {showFullImage && review.imageUrl && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.9)',
            zIndex: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setShowFullImage(false)}
        >
          <button
            onClick={() => setShowFullImage(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <Icons.X size={24} />
          </button>
          <img
            src={review.imageUrl}
            alt="Customer review"
            style={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

const FloatingLeaves = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if mobile on mount and window resize
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Don't render on mobile or if user prefers reduced motion
  if (isMobile || prefersReducedMotion()) return null;

  const leafSvg = (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      width="20"
      height="20"
      style={{ color: colors.gold400 }}
    >
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </svg>
  );

  return (
    <div className="floating-leaves">
      {[...Array(10)].map((_, i) => (
        <div key={i} className="floating-leaf">{leafSvg}</div>
      ))}
    </div>
  );
};

// Welcome/Onboarding Screen Component — first visit only
const WelcomeScreen = ({ onDismiss }) => {
  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#fff',
      }}
    >
      {/* Hero Image — full screen */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
      }}>
        <img
          src="/hero-splash.png"
          alt="Chámomile Oatmilk Tea"
          onContextMenu={(e) => e.preventDefault()}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
        />
      </div>

      {/* Half-round white fade-away background */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '55%',
          background: 'radial-gradient(120% 100% at 50% 100%, rgba(255,255,255,1) 45%, rgba(255,255,255,0.7) 75%, transparent 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          padding: '40px 28px 48px',
          textAlign: 'center',
        }}
      >
        <h1 style={{
          fontSize: '32px',
          fontWeight: 800,
          color: colors.stone900,
          lineHeight: 1.15,
          margin: '0 0 12px',
          fontFamily: '"Inter", -apple-system, sans-serif',
          letterSpacing: '-0.3px',
        }}>
          Fragrant. Healthy.<br />
          <span style={{ color: colors.gold400 }}>Guilt-Free.</span>
        </h1>
        <p style={{
          fontSize: '15px',
          color: colors.stone500,
          margin: '0 0 32px',
          lineHeight: 1.5,
          fontWeight: 500,
        }}>
          Fragrant oatmilk teas, brewed with love.
        </p>

        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={onDismiss}
          style={{
            width: '100%',
            padding: '18px',
            background: colors.black50,
            color: '#fff',
            border: 'none',
            borderRadius: '16px',
            fontSize: '16px',
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.3px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }}
        >
          Next
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// iOS-Compatible Calendar Picker Component
const CalendarPicker = ({ value, onChange, minDate, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const min = new Date(minDate);
    return new Date(min.getFullYear(), min.getMonth(), 1);
  });

  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const minDateObj = new Date(minDate);
  minDateObj.setHours(0, 0, 0, 0);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return 'Select date';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    date.setHours(0, 0, 0, 0);
    return date < minDateObj;
  };

  const isDateSelected = (date) => {
    if (!date || !value) return false;
    return formatDateValue(date) === value;
  };

  const handleDateClick = (date) => {
    if (!isDateDisabled(date)) {
      onChange(formatDateValue(date));
      setIsOpen(false);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const canNavigatePrev = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const lastDayOfPrev = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0);
    return lastDayOfPrev >= minDateObj;
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <>
      {/* Date Input Button */}
      <div
        onClick={() => setIsOpen(true)}
        style={{
          ...styles.input,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          color: value ? colors.stone800 : colors.stone400,
          background: '#fff',
        }}
      >
        <span>{formatDateDisplay(value)}</span>
        <Icons.Calendar />
      </div>

      {/* Calendar Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
            }}
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff',
                borderRadius: '20px',
                padding: '20px',
                width: '100%',
                maxWidth: '360px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              }}
            >
              {/* Month Navigation */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
              }}>
                <button
                  onClick={() => canNavigatePrev() && navigateMonth(-1)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    border: 'none',
                    background: canNavigatePrev() ? colors.stone100 : 'transparent',
                    cursor: canNavigatePrev() ? 'pointer' : 'default',
                    color: canNavigatePrev() ? colors.stone700 : colors.stone300,
                    fontSize: '18px',
                  }}
                >
                  ‹
                </button>
                <span style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: colors.stone800,
                }}>
                  {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <button
                  onClick={() => navigateMonth(1)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    border: 'none',
                    background: colors.stone100,
                    cursor: 'pointer',
                    color: colors.stone700,
                    fontSize: '18px',
                  }}
                >
                  ›
                </button>
              </div>

              {/* Week Days Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '4px',
                marginBottom: '8px',
              }}>
                {weekDays.map(day => (
                  <div key={day} style={{
                    textAlign: 'center',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: colors.stone400,
                    padding: '8px 0',
                  }}>
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '4px',
              }}>
                {days.map((date, idx) => (
                  <button
                    key={idx}
                    onClick={() => date && handleDateClick(date)}
                    disabled={isDateDisabled(date)}
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      borderRadius: '50%',
                      border: 'none',
                      background: isDateSelected(date)
                        ? `linear-gradient(135deg, ${colors.gold400}, ${colors.gold500})`
                        : 'transparent',
                      color: isDateSelected(date)
                        ? '#fff'
                        : isDateDisabled(date)
                          ? colors.stone300
                          : colors.stone700,
                      cursor: isDateDisabled(date) ? 'default' : 'pointer',
                      fontSize: '14px',
                      fontWeight: isDateSelected(date) ? 600 : 400,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {date ? date.getDate() : ''}
                  </button>
                ))}
              </div>

              {/* Cancel Button */}
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  marginTop: '16px',
                  background: colors.stone100,
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.stone600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Tea Cup Loading Spinner Component
const TeaCupSpinner = ({ text = 'Loading...' }) => (
  <div className="teacup-spinner">
    <div className="teacup-steam">
      <div className="steam-line" />
      <div className="steam-line" />
      <div className="steam-line" />
    </div>
    <div className="teacup-icon">🍵</div>
    <p style={{ fontSize: '14px', color: colors.stone500, marginTop: '8px' }}>{text}</p>
  </div>
);

// Celebration Success Component
const CelebrationSuccess = ({ title, subtitle }) => {
  useEffect(() => {
    if (!prefersReducedMotion()) {
      createConfetti(0.5, 0.3);
    }
  }, []);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={successVariants}
      style={{ textAlign: 'center' }}
    >
      <div className="celebration-check">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <motion.h2
        variants={fadeInUp}
        style={{ fontSize: '24px', fontWeight: 700, marginTop: '20px', marginBottom: '8px' }}
      >
        {title}
      </motion.h2>
      <motion.p
        variants={fadeInUp}
        style={{ color: colors.stone500 }}
      >
        {subtitle}
      </motion.p>
    </motion.div>
  );
};

// ============================================
// SIZED-PRODUCT VARIANT GROUPING
// ============================================
// Each size remains its own Firestore product (so POS, orders and reports keep
// keying off one product ID per line item), but products carry explicit
// `sizeGroup` and `size` fields set by the POS size selector. We group on those
// fields and add the SELECTED size's real product ID to the cart, so the
// customer sees one card instead of four near-identical listings.
//
// Grouping is driven ONLY by those fields — there is no name parsing, so a
// product's name can never affect how it is grouped.

const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'];

const sizeRank = (size) => {
  const i = SIZE_ORDER.indexOf(String(size).toUpperCase());
  return i === -1 ? SIZE_ORDER.length : i;
};

/**
 * Turn a flat product list into display items, collapsing sized products into
 * one variant group. A group only collapses when it has 2+ sizes, so a lone
 * sized product still renders as an ordinary card.
 */
const groupProductVariants = (products) => {
  const groups = new Map();
  const order = [];

  products.forEach(p => {
    const group = (p.sizeGroup || '').trim();
    const size = (p.size || '').trim();

    if (!group || !size) {
      order.push({ type: 'single', product: p });
      return;
    }

    const key = group.toLowerCase();
    if (!groups.has(key)) {
      const entry = { type: 'group', baseName: group, variants: [] };
      groups.set(key, entry);
      order.push(entry);
    }
    groups.get(key).variants.push({ ...p, size: size.toUpperCase() });
  });

  // Collapse only real groups; demote single-variant "groups" back to singles.
  return order.flatMap(item => {
    if (item.type !== 'group') return [item];
    if (item.variants.length < 2) {
      return item.variants.map(v => ({ type: 'single', product: v }));
    }
    item.variants.sort((a, b) => sizeRank(a.size) - sizeRank(b.size));
    return [item];
  });
};

const PrivacyNotice = ({ onShowPrivacy }) => (
  <div style={{
    position: 'fixed',
    bottom: '100px',
    left: '16px',
    zIndex: 40,
    opacity: 0.7,
    transition: 'opacity 0.2s ease'
  }}>
    <button
      onClick={onShowPrivacy}
      style={{
        background: 'rgba(255,255,255,0.9)',
        border: `1px solid ${colors.stone200}`,
        borderRadius: '20px',
        padding: '6px 12px',
        fontSize: '10px',
        color: colors.stone500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Icons.Shield size={12} /> Privacy
    </button>
  </div>
);

// FIX #2: Enhanced injectStyles with DNS prefetch and preconnect hints
const injectStyles = () => {
  if (document.getElementById('chamomile-animations')) return;

  // Add DNS prefetch and preconnect for Imgur (product images)
  if (!document.querySelector('link[href="https://i.imgur.com"]')) {
    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = 'https://i.imgur.com';
    document.head.appendChild(dnsPrefetch);

    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://i.imgur.com';
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);
  }

  const style = document.createElement('style');
  style.id = 'chamomile-animations';
  style.textContent = `
    @keyframes shimmer {
      0% { left: -100%; }
      100% { left: 100%; }
    }
    @keyframes pop {
      0% { transform: scale(0); }
      50% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes glow-pulse {
      0%, 100% { 
        box-shadow: 0 0 5px rgba(184, 148, 61, 0.4), 0 0 20px rgba(184, 148, 61, 0.2), 0 4px 15px rgba(0,0,0,0.1);
      }
      50% { 
        box-shadow: 0 0 20px rgba(184, 148, 61, 0.6), 0 0 40px rgba(184, 148, 61, 0.4), 0 4px 20px rgba(0,0,0,0.15);
      }
    }
    .product-grid { animation: fadeIn 0.5s ease-out; }
    .product-card-enter { animation: slideUp 0.4s ease-out forwards; }
    .order-now-btn { 
      animation: glow-pulse 2s ease-in-out infinite;
      transition: transform 0.2s ease;
    }
    .order-now-btn:hover { 
      animation: none;
      transform: scale(1.05);
      box-shadow: 0 0 25px rgba(184, 148, 61, 0.7), 0 0 50px rgba(184, 148, 61, 0.5), 0 6px 25px rgba(0,0,0,0.2);
    }
    details summary::-webkit-details-marker { display: none; }
    details summary { list-style: none; }
    details[open] summary span:last-child { display: none; }
  `;
  document.head.appendChild(style);

  const existingFavicon = document.querySelector('link[rel="icon"]');
  if (existingFavicon) existingFavicon.remove();
  const favicon = document.createElement('link');
  favicon.rel = 'icon';
  favicon.type = 'image/png';
  favicon.href = '/favicon-32x32.png';
  document.head.appendChild(favicon);

  const shortcutIcon = document.createElement('link');
  shortcutIcon.rel = 'shortcut icon';
  shortcutIcon.type = 'image/png';
  shortcutIcon.href = '/favicon-32x32.png';
  document.head.appendChild(shortcutIcon);
};

const TermsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  const sectionStyle = { marginBottom: '20px' };
  const headingStyle = { fontSize: '14px', fontWeight: 600, color: colors.stone800, marginBottom: '8px' };
  const listStyle = { paddingLeft: '20px', margin: 0 };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '20px', maxWidth: '650px', width: '100%', maxHeight: '90vh', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.stone100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Terms of Service</h2>
            <p style={{ fontSize: '11px', color: colors.stone400, margin: '4px 0 0' }}>Terma Perkhidmatan</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.stone400 }}><Icons.X size={20} /></button>
        </div>
        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(90vh - 160px)', fontSize: '13px', color: colors.stone600, lineHeight: 1.7 }}>
          <p style={{ color: colors.stone400, fontSize: '11px', marginBottom: '20px' }}>Last updated: 2 March 2026</p>

          <section style={sectionStyle}><h3 style={headingStyle}>1. Introduction & Acceptance</h3><p>Welcome to Chámomile ("we", "us", "our"). These Terms of Service ("Terms") govern your use of our website, online ordering platform, and all related services (collectively, the "Service"). By accessing our website or placing an order, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Service.</p></section>

          <section style={sectionStyle}><h3 style={headingStyle}>2. Order Placement</h3><ul style={listStyle}><li>All orders are subject to availability and confirmation of the order price.</li><li>Minimum order quantity is <strong>5 bottles per flavour</strong> for quality assurance and optimal freshness.</li><li>Quantities must be in <strong>multiples of 5 bottles</strong> per flavour.</li><li>We reserve the right to refuse or cancel any order at our discretion, including but not limited to cases of suspected fraud, pricing errors, or product unavailability.</li><li>Order confirmation will be sent via WhatsApp upon successful payment verification.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>3. Payment Terms</h3><ul style={listStyle}><li>Payment must be made via <strong>bank transfer</strong> or <strong>DuitNow QR</strong> to our designated Maybank account.</li><li>Please use your <strong>phone number</strong> as the payment reference for verification purposes.</li><li>A payment receipt or screenshot must be uploaded through the ordering platform before confirming your order.</li><li>Your order will <strong>not be confirmed</strong> until payment has been received and verified by our team.</li><li>If payment is not received within <strong>48 hours</strong> of order placement, your order will be automatically cancelled.</li><li>All prices displayed on our platform are in <strong>Malaysian Ringgit (RM)</strong> and are inclusive of applicable taxes where required.</li><li>We do not currently accept credit/debit card payments or cash on delivery.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>4. Bulk Discounts</h3><ul style={listStyle}><li><strong>5 bottles (single flavour):</strong> RM1 discount per bottle.</li><li><strong>10 or more bottles (any mix of flavours):</strong> RM2 discount per bottle.</li><li>Discount tiers are automatically calculated and applied at checkout.</li><li>Bulk discounts may be combined with valid promotional codes unless otherwise stated.</li><li>We reserve the right to modify discount tiers at any time without prior notice.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>5. Collection & Delivery</h3><p style={{ fontWeight: 600, marginBottom: '6px' }}>Self-Pickup:</p><ul style={listStyle}><li>Self-pickup is available at <strong>Forest Hill Residences, Kota Kinabalu, Sabah</strong>.</li><li>A minimum of <strong>1 day advance notice</strong> is required for all orders.</li><li>Pickup hours: <strong>10:00 AM – 10:00 PM daily</strong>.</li><li>Please present your Order ID (via WhatsApp) upon collection for verification.</li><li>Self-pickup is complimentary (no additional charges).</li><li>Uncollected orders without prior notice may be forfeited after 24 hours.</li></ul><p style={{ fontWeight: 600, marginBottom: '6px', marginTop: '12px' }}>Grab Express Delivery:</p><ul style={listStyle}><li>Delivery via Grab Express is available and will be arranged by the customer.</li><li>Delivery fees are borne by the customer and are payable directly to the delivery service provider.</li><li>Chámomile is not liable for any delays, damages, or losses incurred during third-party delivery.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>6. Product Quality & Storage</h3><ul style={listStyle}><li>All products are <strong>made fresh in small batches</strong> using natural botanicals and premium oatmilk.</li><li>Products are best consumed <strong>immediately upon opening</strong> for optimal flavour and quality.</li><li>Store all drinks in the <strong>refrigerator (0–4°C)</strong> to maintain freshness.</li><li>Unopened shelf-life: up to <strong>3 days refrigerated</strong> (general).</li><li>Sun-Kissed Peach variant: up to <strong>2 weeks refrigerated</strong> (unopened).</li><li>We do not use artificial preservatives; product appearance and taste may vary slightly between batches due to the use of natural ingredients.</li><li>Allergen notice: Our products contain <strong>oat milk</strong>. Please check individual product descriptions for specific ingredients.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>7. Returns & Refunds</h3><ul style={listStyle}><li>Due to the perishable nature of our fresh products, we <strong>do not accept returns</strong> once products have been collected or delivered.</li><li>If you receive a damaged or defective product, please contact us within <strong>1 hour of receipt</strong> with photographic evidence via WhatsApp.</li><li>All complaints will be reviewed on a case-by-case basis.</li><li>Approved refunds will be processed via bank transfer within <strong>7–14 business days</strong>.</li><li>Refunds will not be issued for change of mind, incorrect orders placed by the customer, or products that have been opened.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>8. Cancellation Policy</h3><ul style={listStyle}><li>Orders may be cancelled <strong>free of charge before payment confirmation</strong>.</li><li>After payment has been confirmed, cancellations must be requested <strong>at least 24 hours before</strong> the scheduled collection time.</li><li>Late cancellations (less than 24 hours before collection) will incur a <strong>RM3.00 processing fee</strong>, which will be deducted from the refund amount.</li><li>No-shows without prior cancellation notice will not be eligible for a refund.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>9. Account & Loyalty Program</h3><ul style={listStyle}><li>You may create an account to track orders and participate in our loyalty programme.</li><li>Loyalty points are earned at a rate of <strong>1 point per RM1.00 spent</strong>.</li><li>Points may be redeemed for discounts at the rate of <strong>100 points = RM1.00</strong>.</li><li>Loyalty tiers: 🍃 Tea Leaf (0–99 pts), 🌸 Blossom (100–499 pts), 👑 Royal Blend (500–999 pts), 💎 Grand Cru (1,000+ pts).</li><li>You are responsible for maintaining the accuracy of your account information.</li><li>We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.</li><li>Loyalty points have no cash value and are non-transferable.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>10. Promotional Codes</h3><ul style={listStyle}><li>Promotional codes are subject to their specific terms, conditions, and expiry dates.</li><li>Unless otherwise stated, only one promotional code may be applied per order.</li><li>Promotional codes cannot be exchanged for cash and may not be combined with certain other offers.</li><li>We reserve the right to revoke or modify promotional codes at any time.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>11. Intellectual Property</h3><p>All content on our platform — including but not limited to the Chámomile brand name, logo, product names, images, text, designs, and source code — is the property of Chámomile and is protected by applicable intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from our content without prior written consent.</p></section>

          <section style={sectionStyle}><h3 style={headingStyle}>12. Limitation of Liability</h3><ul style={listStyle}><li>Chámomile provides its products and services on an "as is" and "as available" basis.</li><li>To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, consequential, or punitive damages arising from your use of our Service.</li><li>Our total liability for any claim arising from or related to these Terms shall not exceed the total amount you paid for the relevant order.</li><li>We are not responsible for interruptions to the Service caused by factors beyond our reasonable control, including but not limited to internet connectivity issues, server failures, or force majeure events.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>13. Modifications to These Terms</h3><p>We reserve the right to update or modify these Terms at any time. Changes will be effective immediately upon posting the revised Terms on our platform. The "Last updated" date at the top of this document indicates when these Terms were most recently revised. Your continued use of the Service after any changes constitutes your acceptance of the updated Terms.</p></section>

          <section style={sectionStyle}><h3 style={headingStyle}>14. Contact Information</h3><ul style={listStyle}><li><strong>WhatsApp (Preferred):</strong> 010-820 2187</li><li><strong>Email:</strong> gb.chamomile@gmail.com</li><li><strong>Address:</strong> Forest Hill Residences, Kota Kinabalu, Sabah, Malaysia</li></ul></section>

          <section style={{ marginBottom: '8px' }}><h3 style={headingStyle}>15. Governing Law</h3><p>These Terms shall be governed by and construed in accordance with the laws of Malaysia. Any disputes arising from or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Sabah, Malaysia.</p></section>
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${colors.stone100}`, background: colors.stone50 }}><button onClick={onClose} style={{ ...styles.btnPrimary, width: '100%' }}>I Understand</button></div>
      </div>
    </div>
  );
};

const PrivacyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  const sectionStyle = { marginBottom: '20px' };
  const headingStyle = { fontSize: '14px', fontWeight: 600, color: colors.stone800, marginBottom: '8px' };
  const listStyle = { paddingLeft: '20px', margin: 0 };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '20px', maxWidth: '650px', width: '100%', maxHeight: '90vh', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.stone100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Privacy Policy & Cookie Notice</h2>
            <p style={{ fontSize: '11px', color: colors.stone400, margin: '4px 0 0' }}>Dasar Privasi & Notis Kuki</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.stone400 }}><Icons.X size={20} /></button>
        </div>
        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(90vh - 160px)', fontSize: '13px', color: colors.stone600, lineHeight: 1.7 }}>
          <p style={{ color: colors.stone400, fontSize: '11px', marginBottom: '20px' }}>Last updated / Kemas kini terakhir: 2 March 2026</p>

          <section style={sectionStyle}><h3 style={headingStyle}>1. Introduction</h3><p>Chámomile ("we", "us", "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our website, online ordering platform, and related services (the "Service"). By using our Service, you consent to the practices described in this policy.</p></section>

          <section style={sectionStyle}><h3 style={headingStyle}>2. Information We Collect</h3><p style={{ fontWeight: 600, marginBottom: '6px' }}>Information you provide directly:</p><ul style={listStyle}><li><strong>Name:</strong> To identify you and personalise your experience.</li><li><strong>Phone number:</strong> For order confirmation via WhatsApp and account identification.</li><li><strong>Email address:</strong> (Optional) For account creation and communication.</li><li><strong>Payment receipts:</strong> Uploaded images of payment proof for order verification.</li></ul><p style={{ fontWeight: 600, marginBottom: '6px', marginTop: '12px' }}>Information collected automatically:</p><ul style={listStyle}><li><strong>Order history:</strong> Products ordered, quantities, dates, and amounts.</li><li><strong>Loyalty points:</strong> Points earned, redeemed, and current balance.</li><li><strong>Device information:</strong> Browser type and version for compatibility purposes.</li><li><strong>Usage data:</strong> Pages visited and features used, to improve our Service.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>3. How We Use Your Information</h3><p>We use the information we collect to:</p><ul style={listStyle}><li><strong>Process your orders:</strong> Verify payments, confirm orders, and coordinate collection/delivery.</li><li><strong>Manage your account:</strong> Maintain your profile, loyalty points, and order history.</li><li><strong>Communicate with you:</strong> Send order confirmations, updates, and respond to enquiries via WhatsApp.</li><li><strong>Operate and improve our Service:</strong> Analyse usage patterns, fix issues, and enhance user experience.</li><li><strong>Administer promotions:</strong> Apply and track promotional codes and discounts.</li><li><strong>Process reviews:</strong> Display and moderate customer reviews and ratings.</li><li><strong>Comply with legal obligations:</strong> Meet applicable Malaysian legal and regulatory requirements.</li></ul><p style={{ marginTop: '10px' }}>We will <strong>never</strong> sell, rent, or share your personal information with third parties for their marketing purposes.</p></section>

          <section style={{ ...sectionStyle, background: colors.stone50, borderRadius: '12px', padding: '16px', border: `1px solid ${colors.stone100}` }}><h3 style={{ ...headingStyle, display: 'flex', alignItems: 'center', gap: '8px' }}><Icons.Shield size={16} /> 4. Cookie Notice</h3><p>We use <strong>ONLY strictly necessary (essential) first-party cookies</strong>. These cookies are required for our website to function and to provide the service you have requested.</p><p style={{ fontWeight: 600, marginBottom: '6px', marginTop: '10px' }}>Cookies we use:</p><ul style={listStyle}><li><strong>chamomile_cart:</strong> Stores your shopping cart contents so they persist between page loads. Expires after 7 days.</li><li><strong>chamomile_customer:</strong> Saves your contact information for convenience on repeat orders. Expires after 7 days.</li><li><strong>chamomile_cookie_consent:</strong> Records your cookie consent preference. Expires after 365 days.</li></ul><p style={{ marginTop: '10px' }}>We do <strong>not</strong> use any analytics, advertising, or third-party tracking cookies. No data from our cookies is shared with any external parties.</p></section>

          <section style={sectionStyle}><h3 style={headingStyle}>5. Data Sharing & Third Parties</h3><ul style={listStyle}><li>Your data is stored securely on <strong>Google Firebase</strong> (Firestore database and Firebase Storage) hosted in Google's cloud infrastructure.</li><li>Payment receipt images are stored in <strong>Firebase Storage</strong> with secure access controls.</li><li>We do not share your personal data with any third parties for marketing, advertising, or profiling purposes.</li><li>We may disclose your information if required by law or to protect our rights, property, or safety.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>6. Data Security</h3><ul style={listStyle}><li>All data transmitted between your browser and our servers is encrypted using <strong>HTTPS/TLS</strong>.</li><li>Firebase provides enterprise-grade security, including encryption at rest and in transit.</li><li>Access to customer data is restricted to authorised personnel only.</li><li>Payment receipt images are stored with unique identifiers and are not publicly accessible.</li><li>While we take reasonable measures to protect your data, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>7. Data Retention</h3><ul style={listStyle}><li><strong>Account and order data:</strong> Retained for as long as your account is active, or as needed to provide our services.</li><li><strong>Order history:</strong> Retained for a minimum of <strong>7 years</strong> for accounting and legal compliance purposes.</li><li><strong>Payment receipts:</strong> Retained for <strong>12 months</strong> after order completion, then automatically deleted.</li><li><strong>Cookies:</strong> Essential cookies expire within 7 days (cart, customer info) or 365 days (consent).</li><li>You may request deletion of your data at any time by contacting us.</li></ul></section>

          <section style={sectionStyle}><h3 style={headingStyle}>8. Your Rights</h3><p>Under applicable Malaysian data protection laws (Personal Data Protection Act 2010), you have the right to:</p><ul style={listStyle}><li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li><li><strong>Correction:</strong> Request correction of any inaccurate or incomplete data.</li><li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements).</li><li><strong>Withdrawal of consent:</strong> Withdraw your consent to data processing at any time, which may affect our ability to provide certain services.</li><li><strong>Complaint:</strong> Lodge a complaint with the relevant data protection authority if you believe your rights have been violated.</li></ul><p style={{ marginTop: '10px' }}>To exercise any of these rights, please contact us via WhatsApp at 010-820 2187 or email gb.chamomile@gmail.com.</p></section>

          <section style={sectionStyle}><h3 style={headingStyle}>9. Children's Privacy</h3><p>Our Service is not directed to individuals under the age of 13. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal data, please contact us immediately so we can take steps to remove such information.</p></section>

          <section style={sectionStyle}><h3 style={headingStyle}>10. Changes to This Policy</h3><p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. We will update the "Last updated" date at the top of this policy when changes are made. We encourage you to review this policy periodically. Your continued use of the Service after any modifications constitutes your acceptance of the updated policy.</p></section>

          <section style={{ marginBottom: '8px' }}><h3 style={headingStyle}>11. Contact Information</h3><p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p><ul style={listStyle}><li><strong>WhatsApp (Preferred):</strong> 010-820 2187</li><li><strong>Email:</strong> gb.chamomile@gmail.com</li><li><strong>Address:</strong> Forest Hill Residences, Kota Kinabalu, Sabah, Malaysia</li></ul></section>
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${colors.stone100}`, background: colors.stone50 }}><button onClick={onClose} style={{ ...styles.btnPrimary, width: '100%' }}>I Understand / Saya Faham</button></div>
      </div>
    </div>
  );
};

const ReviewSubmitModal = ({ isOpen, onClose, product, onSubmitSuccess }) => {
  const [rating, setRating] = useState(0);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [orderId, setOrderId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const photoInputRef = useRef(null);

  const generateDiscountCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = 'THANKS';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      setError('Please select a JPG, PNG, HEIC, or WebP image');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be under 10MB');
      return;
    }
    setPhotoFile(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result);
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview('');
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const uploadPhotoToStorage = async (file, reviewId) => {
    if (!storage) { console.warn('Firebase Storage not configured'); return null; }
    try {
      const compressedFile = await ImageUtils.compressImage(file);
      const ext = 'jpg';
      const fileName = `review-images/${reviewId}-${Date.now()}.${ext}`;
      const storageRef = ref(storage, fileName);
      setUploadProgress('Uploading photo...');
      await uploadBytes(storageRef, compressedFile);
      setUploadProgress('Processing...');
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading photo:', error);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (rating === 0) { setError('Please select a rating'); return; }
    if (!customerName.trim()) { setError('Please enter your name'); return; }
    if (reviewComment.length < 10) { setError('Review must be at least 10 characters'); return; }
    if (reviewComment.length > 500) { setError('Review must be under 500 characters'); return; }
    setSubmitting(true);
    setUploadProgress('');
    try {
      const reviewId = `REV-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
      let imageUrl = null;
      if (photoFile) {
        imageUrl = await uploadPhotoToStorage(photoFile, reviewId);
        if (!imageUrl && photoFile) console.warn('Photo upload failed, continuing without image');
      }
      setUploadProgress('Submitting review...');
      const result = await submitReview({
        productId: product.id,
        productName: product.name,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim() || undefined,
        rating,
        title: reviewTitle.trim() || undefined,
        comment: reviewComment.trim(),
        orderId: orderId.trim() || undefined,
        imageUrl,
      });
      setSubmitting(false);
      setUploadProgress('');
      if (result.success) {
        if (rating >= 4 && photoFile) {
          const code = generateDiscountCode();
          setDiscountCode(code);
          if (db) {
            try {
              await addDoc(collection(db, 'promos'), {
                code: code,
                type: 'percentage',
                value: 10,
                description: `Review Thank You - ${product.name}`,
                maxUses: 1,
                usedCount: 0,
                minOrder: 0,
                active: true,
                startDate: serverTimestamp(),
                endDate: null,
                source: 'review',
                linkedReviewId: result.reviewId,
                customerName: customerName.trim(),
                customerPhone: customerPhone.trim() || null,
                productId: product.id,
                productName: product.name,
                createdAt: serverTimestamp(),
              });
            } catch (promoErr) { console.warn('Could not save promo code to Firebase:', promoErr); }
          }
        }
        setSuccess(true);
        onSubmitSuccess && onSubmitSuccess();
      } else {
        setError(result.error || 'Failed to submit review');
      }
    } catch (err) {
      console.error('Review submission error:', err);
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  const handleClose = () => {
    setRating(0);
    setCustomerName('');
    setCustomerPhone('');
    setReviewTitle('');
    setReviewComment('');
    setOrderId('');
    setError('');
    setSuccess(false);
    setPhotoFile(null);
    setPhotoPreview('');
    setUploadProgress('');
    setDiscountCode('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={handleClose}>
      <div style={{ background: '#fff', borderRadius: '20px', maxWidth: '450px', width: '100%', maxHeight: '90vh', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.stone100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>{success ? 'Thank You!' : 'Write a Review'}</h2>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.stone400 }}><Icons.X size={20} /></button>
        </div>
        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: colors.success + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}><Icons.Check size={32} /></div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: colors.stone800 }}>Review Submitted!</h3>
              <p style={{ fontSize: '14px', color: colors.stone500, marginBottom: '16px' }}>Your review for {product.name} has been submitted and is awaiting approval.</p>
              {discountCode && (
                <div style={{ background: `linear-gradient(135deg, ${colors.gold50}, ${colors.gold100})`, border: `2px dashed ${colors.gold400}`, borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                  <p style={{ fontSize: '12px', color: colors.gold700, margin: '0 0 8px', fontWeight: 600 }}>🎉 YOUR 10% DISCOUNT CODE</p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px', fontWeight: 700, color: colors.gold600, fontFamily: 'monospace', letterSpacing: '2px' }}>{discountCode}</span>
                    <button onClick={() => navigator.clipboard.writeText(discountCode)} style={{ background: colors.gold200, border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: colors.gold700 }}><Icons.Copy size={14} /> Copy</button>
                  </div>
                </div>
              )}
              <button onClick={handleClose} style={{ ...styles.btnPrimary, width: '100%' }}>Done</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ background: `linear-gradient(135deg, ${colors.gold50}, ${colors.gold100})`, border: `1px solid ${colors.gold200}`, borderRadius: '12px', padding: '12px 14px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>🎁</span>
                <div><p style={{ fontSize: '13px', fontWeight: 600, color: colors.gold700, margin: 0 }}>Get 10% Off Your Next Order!</p><p style={{ fontSize: '11px', color: colors.stone500, margin: '2px 0 0' }}>Leave a 4-5 star review with a photo to receive a discount code</p></div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', padding: '12px', background: colors.stone50, borderRadius: '12px' }}>
                <img src={product.imageUrl} alt={product.name} style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
                <div><h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{product.name}</h4><p style={{ fontSize: '12px', color: colors.stone500, margin: '2px 0 0' }}>Share your experience</p></div>
              </div>
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <label style={{ ...styles.label, justifyContent: 'center', marginBottom: '12px' }}>Your Rating *</label>
                <InteractiveStarRating rating={rating} onRatingChange={setRating} />
                <p style={{ fontSize: '12px', color: colors.stone400, marginTop: '8px' }}>{rating === 0 ? 'Tap to rate' : ['', 'We\'re sorry 😔', 'Fair', 'Good', 'Very Good! 💛', 'Amazing! We love you! 💛'][rating]}</p>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}><Icons.User size={12} /> Your Name *</label>
                <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Enter your name" style={styles.input} maxLength={50} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}><Icons.MessageCircle size={12} /> Your Review *</label>
                <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Share your thoughts about this product..." style={{ ...styles.input, minHeight: '100px', resize: 'vertical' }} maxLength={500} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={styles.label}><Icons.Camera size={12} /> Add a Photo (Optional)</label>
                <input type="file" ref={photoInputRef} onChange={handlePhotoSelect} accept="image/*" style={{ display: 'none' }} />
                {photoPreview ? (
                  <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: `2px solid ${colors.success}`, background: colors.success + '10' }}>
                    <img src={photoPreview} alt="Review photo preview" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }} />
                    <button type="button" onClick={handleRemovePhoto} style={{ position: 'absolute', top: '8px', right: '8px', width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><Icons.X size={16} /></button>
                  </div>
                ) : (
                  <div onClick={() => photoInputRef.current?.click()} style={{ border: `2px dashed ${colors.stone300}`, borderRadius: '12px', padding: '24px', textAlign: 'center', cursor: 'pointer', background: colors.stone50 }}>
                    <div style={{ color: colors.stone400, marginBottom: '8px' }}><Icons.Camera size={32} /></div>
                    <p style={{ fontSize: '14px', fontWeight: 500, color: colors.stone600, margin: 0 }}>Tap to add a photo</p>
                  </div>
                )}
              </div>
              {error && <div style={{ ...styles.alert, background: '#fee2e2', border: '1px solid #fecaca', marginBottom: '16px' }}><Icons.Alert size={16} style={{ color: colors.error, flexShrink: 0 }} /><span style={{ color: colors.error }}>{error}</span></div>}
              <button type="submit" style={{ ...styles.btnPrimary, width: '100%', opacity: submitting ? 0.7 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }} disabled={submitting}>{submitting ? (uploadProgress || 'Submitting...') : 'Submit Review'}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductReviewsModal = ({ isOpen, onClose, product, onWriteReview }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingData, setRatingData] = useState(null);

  useEffect(() => {
    if (isOpen && product) { fetchReviews(); }
  }, [isOpen, product]);

  const fetchReviews = async () => {
    setLoading(true);
    const productReviews = await getProductReviews(product.id);
    setReviews(productReviews);
    if (productReviews.length > 0) {
      const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      let total = 0;
      productReviews.forEach(r => {
        const rating = Math.round(r.rating);
        if (distribution.hasOwnProperty(rating)) { distribution[rating]++; total += r.rating; }
      });
      setRatingData({ average: total / productReviews.length, total: productReviews.length, distribution });
    } else {
      setRatingData(null);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '20px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.stone100}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src={product.imageUrl} alt={product.name} style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
            <div><h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>{product.name}</h2><p style={{ fontSize: '12px', color: colors.stone500, margin: '2px 0 0' }}>Customer Reviews</p></div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.stone400 }}><Icons.X size={20} /></button>
        </div>
        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(90vh - 200px)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: colors.stone400 }}>Loading reviews...</div>
          ) : reviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: colors.stone100, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: colors.stone400 }}><Icons.MessageCircle size={28} /></div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: colors.stone700 }}>No Reviews Yet</h3>
              <p style={{ fontSize: '13px', color: colors.stone500, marginBottom: '20px' }}>Be the first to share your experience!</p>
            </div>
          ) : (
            <>
              {ratingData && (
                <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', padding: '16px', background: colors.stone50, borderRadius: '12px', alignItems: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '36px', fontWeight: 700, color: colors.stone800 }}>{ratingData.average.toFixed(1)}</div>
                    <StarRating rating={ratingData.average} showCount={false} size={14} />
                    <p style={{ fontSize: '12px', color: colors.stone500, marginTop: '4px' }}>{ratingData.total} review{ratingData.total !== 1 ? 's' : ''}</p>
                  </div>
                  <div style={{ flex: 1 }}>
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = ratingData.distribution[star];
                      const percentage = (count / ratingData.total) * 100;
                      return (
                        <div key={star} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', color: colors.stone500, width: '12px' }}>{star}</span>
                          <div style={{ flex: 1, height: '6px', background: colors.stone200, borderRadius: '3px', overflow: 'hidden' }}><div style={{ width: `${percentage}%`, height: '100%', background: colors.gold500, borderRadius: '3px', transition: 'width 0.3s ease' }} /></div>
                          <span style={{ fontSize: '11px', color: colors.stone400, width: '24px' }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <PinterestMasonryGrid reviews={reviews} />
            </>
          )}
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${colors.stone100}`, background: colors.stone50 }}>
          <button onClick={onClose} style={{ ...styles.btnSecondary, width: '100%' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

const WhatsAppRedirectModal = ({ isOpen, onClose, whatsappUrl, message, phone, colors }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopyMessage = async () => {
    const success = await BrowserUtils.copyToClipboard(message);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
      <div style={{ background: '#fff', borderRadius: '20px', padding: '24px', maxWidth: '400px', width: '100%', maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}><span style={{ fontSize: '28px' }}>💬</span></div>
          <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>Send Order via WhatsApp</h3>
          <p style={{ fontSize: '13px', color: '#78716c' }}>Tap the button below to send your order details via WhatsApp</p>
        </div>
        {BrowserUtils.isInstagramBrowser() && (
          <div style={{ background: '#faf8f3', border: '1px solid #ebe0cc', borderRadius: '12px', padding: '12px', marginBottom: '16px' }}>
            <p style={{ fontSize: '12px', color: '#846628', margin: 0 }}><strong>📱 Instagram Browser Detected</strong><br />For best results, copy the message and paste it in WhatsApp manually.</p>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button onClick={() => window.location.href = whatsappUrl} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px 28px', fontSize: '14px', fontWeight: 600, color: '#fff', background: '#25D366', border: 'none', borderRadius: '999px', cursor: 'pointer', boxShadow: '0 4px 16px rgba(37,211,102,0.3)' }}><span>📱</span> Open WhatsApp</button>
          <button onClick={handleCopyMessage} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px 28px', fontSize: '14px', fontWeight: 500, color: '#57534e', background: 'transparent', border: '2px solid #e7e5e4', borderRadius: '999px', cursor: 'pointer' }}>📋 {copied ? '✓ Copied!' : 'Copy Message'}</button>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', fontSize: '13px', color: '#78716c', textDecoration: 'none' }}>🔗 Open in new tab</a>
        </div>
        <button onClick={onClose} style={{ width: '100%', padding: '12px', marginTop: '16px', background: 'none', border: 'none', fontSize: '14px', color: '#a8a29e', cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  );
};

// ============================================
// MAIN APPLICATION
// ============================================

export default function ChamomileApp() {
  if (window.location.pathname === '/review') {
    return <ReviewPage />;
  }

  const [step, setStep] = useState(0);
  const [cart, setCart] = useState({});
  // Set when a saved cart referenced a product that no longer exists, so the
  // customer is told why their cart shrank instead of just seeing a smaller total.
  const [staleCartNotice, setStaleCartNotice] = useState(false);
  const [delivery, setDelivery] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [info, setInfo] = useState({ name: '', phone: '', email: '' });

  // Dynamic products from Firestore
  const [products, setProducts] = useState(fallbackProducts);
  const [productsLoading, setProductsLoading] = useState(true);
  const [storeCategories, setStoreCategories] = useState(['Beverage', 'Merch']);
  const [categoryIcons, setCategoryIcons] = useState({
    'Signature': '🍵', 'Classic': '☕', 'Viral': '🔥', 'Seasonal': '🌸',
    'All': '✨', 'Our Menu': '📋',
  });
  const [categoryImages, setCategoryImages] = useState({});
  // A category's TYPE decides cart rules (drink vs per-piece), managed in POS
  // Settings → Categories. Names no longer affect behaviour.
  const [categoryKinds, setCategoryKinds] = useState({ Beverage: 'drink', Merch: 'merch', Food: 'food' });
  // Beverage tags — the drink menu tabs, managed in POS Settings.
  const [storeTags, setStoreTags] = useState(['Classic', 'Signature', 'Viral', 'Seasonal']);
  const [tagIcons, setTagIcons] = useState({
    'Signature': '🍵', 'Classic': '☕', 'Viral': '🔥', 'Seasonal': '🌸',
  });
  const [tagImages, setTagImages] = useState({});

  // Auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [memberData, setMemberData] = useState(null); // Loyalty member data from Firestore
  const [showMemberDashboard, setShowMemberDashboard] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);

  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [done, setDone] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  // Field errors on the checkout page stay hidden until Confirm Order is tapped once.
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [copiedKey, setCopiedKey] = useState('');
  const [promoCodes, setPromoCodes] = useState({});
  const fileInputRef = useRef(null);
  const fieldRefs = useRef({});
  const qrBlobRef = useRef(null);

  // Review-related state
  const [productRatings, setProductRatings] = useState(new Map());
  const [showReviewSubmit, setShowReviewSubmit] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showProductReviews, setShowProductReviews] = useState(false);
  const [selectedProductForReview, setSelectedProductForReview] = useState(null);

  // WhatsApp Modal State
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showEventInquiry, setShowEventInquiry] = useState(false);
  const [whatsAppData, setWhatsAppData] = useState({ url: '', message: '', phone: '' });

  // First add confetti state
  const [hasShownFirstAddConfetti, setHasShownFirstAddConfetti] = useState(false);

  // Welcome screen state (first visit only)
  const [showWelcome, setShowWelcome] = useState(() => {
    return !localStorage.getItem('chamomile_welcomed');
  });

  // Search and Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [sortBy, setSortBy] = useState('featured'); // 'featured', 'priceAsc', 'priceDesc', 'nameAsc'

  // Notification state
  const [showNotifications, setShowNotifications] = useState(false);
  const [appNotifications, setAppNotifications] = useState([]); // real-time from Firestore

  // Notification read/dismiss state (persisted to localStorage)
  const [lastSeenTimestamp] = useState(() => parseInt(localStorage.getItem('chamomile_notif_seen') || '0', 10));
  const [dismissedNotifs, setDismissedNotifs] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('chamomile_notif_dismissed') || '[]')); }
    catch { return new Set(); }
  });

  // Derived: unread badge count (new notifications since last panel open)
  const unreadCount = appNotifications.filter(n => {
    if (dismissedNotifs.has(n.id)) return false;
    const ts = n.createdAt?.toDate?.();
    if (!ts) return false;
    if (Date.now() - ts.getTime() > 30 * 24 * 60 * 60 * 1000) return false; // expired
    return ts.getTime() > lastSeenTimestamp;
  }).length;

  const handleOpenNotifications = () => {
    setShowNotifications(true);
    const now = Date.now();
    localStorage.setItem('chamomile_notif_seen', now.toString());
    // Don't mutate state here — React re-renders will pick up the new lastSeenTimestamp on next open
  };

  const dismissNotification = (id) => {
    const next = new Set(dismissedNotifs);
    next.add(id);
    setDismissedNotifs(next);
    localStorage.setItem('chamomile_notif_dismissed', JSON.stringify([...next]));
  };

  // Bottom nav active tab
  const [activeTab, setActiveTab] = useState('home');

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    localStorage.setItem('chamomile_welcomed', 'true');
  }, []);

  // Handler for first item added celebration
  const handleFirstAdd = useCallback(() => {
    if (!hasShownFirstAddConfetti && !prefersReducedMotion()) {
      createConfetti(0.5, 0.6);
      setHasShownFirstAddConfetti(true);
    }
  }, [hasShownFirstAddConfetti]);

  useEffect(() => {
    injectStyles();

    // Load categories and icons
    fetchCategoriesAndIcons()
      .then(res => {
        if (res.categories && res.categories.length > 0) {
          setStoreCategories(res.categories);
        }
        if (res.categoryIcons && Object.keys(res.categoryIcons).length > 0) {
          setCategoryIcons(prev => ({...prev, ...res.categoryIcons}));
        }
        if (res.categoryImages) {
          setCategoryImages(res.categoryImages);
        }
        if (res.categoryKinds) {
          setCategoryKinds(prev => ({ ...prev, ...res.categoryKinds }));
        }
      })
      .catch(err => console.error("Error loading categories", err));

    // Load beverage tags — these become the drink menu tabs
    fetchProductTags()
      .then(res => {
        if (res.tags && res.tags.length > 0) {
          setStoreTags(res.tags);
        }
        if (res.tagIcons && Object.keys(res.tagIcons).length > 0) {
          setTagIcons(prev => ({ ...prev, ...res.tagIcons }));
        }
        if (res.tagImages) {
          setTagImages(res.tagImages);
        }
      })
      .catch(err => console.error("Error loading product tags", err));

    // Load products from Firestore
    fetchOnlineProducts()
      .then(fetched => {
        if (fetched.length > 0) {
          setProducts(fetched);
          ImagePreloader.preloadProductImages(fetched);
        } else {
          ImagePreloader.preloadProductImages(fallbackProducts);
        }
      })
      .catch(() => {
        ImagePreloader.preloadProductImages(fallbackProducts);
      })
      .finally(() => setProductsLoading(false));

    // Load settings from Firestore for global rewards configuration
    const fetchSettings = async () => {
      if (!isFirebaseConfigured() || !db) return;
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          if (data.rewards && Array.isArray(data.rewards)) {
            setGlobalRewards(data.rewards);
          }
        }
      } catch (err) {
        console.error('Error fetching global settings:', err);
      }
    };
    fetchSettings();

    const savedCart = CookieManager.getCart();
    const savedCustomer = CookieManager.getCustomerInfo();
    if (savedCart && Object.keys(savedCart).length > 0) setCart(savedCart);
    if (savedCustomer) setInfo(savedCustomer);
  }, []);

  // ── Real-time notifications listener (no composite index needed) ──
  useEffect(() => {
    if (!db) return;
    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(20)
    );
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter active ones, then filter by tier eligibility client-side
      const active = all.filter(n => n.isActive !== false);
      setAppNotifications(active);
    }, (err) => {
      console.warn('[Notifications] Snapshot error:', err.message);
    });
    return () => unsub();
  }, []); // eslint-disable-line

  // Auth state listener — load member data from `members` collection
  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          // Look up member by authUid, then email
          let member = await getMemberByAuthUid(user.uid);
          if (!member && user.email) {
            member = await getMemberByEmail(user.email);
          }

          if (member) {
            setMemberData(member);
            setInfo(prev => ({
              name: prev.name || member.name || user.displayName || '',
              phone: prev.phone || member.phone || '',
              email: prev.email || member.email || user.email || '',
            }));
          } else {
            setMemberData(null);
            const savedCustomer = CookieManager.getCustomerInfo();
            if (!savedCustomer) {
              setInfo(prev => ({
                name: prev.name || user.displayName || '',
                phone: prev.phone || '',
                email: prev.email || user.email || '',
              }));
            }
          }
        } catch (err) {
          console.error('Error loading member profile:', err);
        }
      } else {
        setMemberData(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch active order for the current user tracker
  useEffect(() => {
    if (!currentUser && !memberData) {
      setActiveOrder(null);
      return;
    }
    const fetchActiveOrder = async () => {
      try {
        const email = currentUser?.email || memberData?.email;
        const phone = memberData?.phone ? memberData.phone.replace(/\D/g, '') : null;
        
        if (!email && !phone) return;

        const onlineOrdersRef = collection(db, 'online-orders');
        let results = [];

        if (email) {
          const qEmail = query(onlineOrdersRef, where('customerInfo.email', '==', email), limit(10));
          const snapEmail = await getDocs(qEmail);
          snapEmail.docs.forEach(d => results.push({ id: d.id, ...d.data() }));
        }

        if (phone && results.length === 0) {
          const qPhone = query(onlineOrdersRef, where('customerInfo.phone', '==', phone), limit(10));
          const snapPhone = await getDocs(qPhone);
          snapPhone.docs.forEach(d => results.push({ id: d.id, ...d.data() }));
        }

        if (results.length > 0) {
          results.sort((a, b) => {
             const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || a.time || 0);
             const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || b.time || 0);
             return dateB - dateA;
          });
          const latestOrder = results[0];
          
          const isRecent = latestOrder.createdAt && (new Date() - latestOrder.createdAt.toDate()) < 48 * 60 * 60 * 1000;
          const isActive = latestOrder.status !== 'completed' && latestOrder.status !== 'cancelled';
          
          if (isActive || isRecent) {
             setActiveOrder(latestOrder);
          } else {
             setActiveOrder(null);
          }
        }
      } catch (err) {
        console.error('Error fetching active order:', err);
      }
    };
    fetchActiveOrder();
  }, [currentUser, memberData, userMenuOpen]);

  // Drop cart entries whose product no longer exists.
  //
  // The cart is restored from a 7-day cookie, so it can outlive the product it
  // points at — and product ids really do change (deleting and re-creating an
  // item in the POS gives it a new id). A stale entry was not harmless:
  //   · subtotal used `?.price || 0`, so it counted as free;
  //   · kindOf() fell through to 'drink' for an unknown id, so it still padded
  //     totalBottles — which is what the bulk discount is priced on, letting a
  //     phantom bottle unlock or inflate a real discount;
  //   · checkout then read `p.name` unguarded and threw, after the order number
  //     had already been consumed and the receipt uploaded.
  // Gated on productsLoading so a slow fetch never wipes a legitimate cart.
  useEffect(() => {
    if (productsLoading || products.length === 0) return;
    const ids = new Set(products.map(p => p.id));
    const stale = Object.keys(cart).filter(id => !ids.has(id));
    if (stale.length === 0) return;

    const known = Object.fromEntries(Object.entries(cart).filter(([id]) => ids.has(id)));
    setCart(known);
    setStaleCartNotice(true);
    // The cart→cookie effect below skips writing an empty cart, so persist here
    // or a fully stale cart would come straight back on the next page load.
    try { CookieManager.saveCart(known); } catch (e) { /* cookie is best-effort */ }
  }, [cart, products, productsLoading]);

  // Fetch ratings when products load
  useEffect(() => {
    if (!productsLoading && products.length > 0) {
      const fetchRatings = async () => {
        const productIds = products.map(p => p.id);
        const ratings = await getAllProductRatings(productIds);
        setProductRatings(ratings);
      };
      fetchRatings();
    }
  }, [products, productsLoading]);

  useEffect(() => {
    if (step === 0 && !orderId) {
      setOrderId(generateOrderId());
      // FIXED: Moved orderService.getNextOrderNumber() to handleSubmit phase
      // to prevent premature incrementing of the order counter
    }
  }, [step, orderId]);

  useEffect(() => { if (Object.keys(cart).length > 0) CookieManager.saveCart(cart); }, [cart]);

  useEffect(() => {
    const fetchPromos = async () => {
      if (!isFirebaseConfigured() || !db) return;
      try {
        const snapshot = await getDocs(collection(db, 'promos'));
        const loaded = {};
        const now = new Date();
        snapshot.forEach((doc) => {
          const d = doc.data();
          if (d.status !== 'active') return;
          if (d.validFrom && new Date(d.validFrom) > now) return;
          if (d.validTo && new Date(d.validTo + 'T23:59:59') < now) return;
          if (d.usageLimit > 0 && d.usageCount >= d.usageLimit) return;
          const code = (d.code || doc.id).toUpperCase();
          loaded[code] = {
            type: d.type || 'percentage',
            value: d.value || 0,
            description: d.name || `${d.value}${d.type === 'percentage' ? '%' : 'RM'} off`,
            minOrder: d.minPurchase || 0,
            maxDiscount: d.maxDiscount || null,
          };
        });
        setPromoCodes(loaded);
      } catch (e) { console.error('Promo fetch error:', e); }
    };
    fetchPromos();
  }, []);

  // Which size is currently selected on each grouped product card, keyed by base name.
  const [selectedVariants, setSelectedVariants] = useState({});

  // ============================================
  // CART KINDS — merch never mixes with drinks
  // ============================================
  // The 5-bottle minimum and the bulk discount exist because drinks are brewed
  // and bottled in batches. Merch has no such constraint, so it must not count
  // toward either.
  //
  // A category's kind comes from POS Settings (settings/categories.kinds), so
  // renaming a category or adding a new one never changes behaviour here and
  // nothing has to be spelled exactly.
  const resolveKind = (category) => {
    const name = (category || '').trim();
    if (!name) return 'drink';
    const explicit = categoryKinds[name]
      || categoryKinds[Object.keys(categoryKinds).find(k => k.toLowerCase() === name.toLowerCase())];
    return ['drink', 'merch', 'food'].includes(explicit) ? explicit : 'drink';
  };

  const isDrinkCategory = (category) => resolveKind(category) === 'drink';

  const kindOf = (id) => resolveKind(products.find(p => p.id === id)?.category);

  const cartEntries = Object.entries(cart);
  const drinkEntries = cartEntries.filter(([id]) => kindOf(id) === 'drink');
  const nonDrinkEntries = cartEntries.filter(([id]) => kindOf(id) !== 'drink');

  // "Bottles" means drinks only — merch and food are deliberately excluded.
  const totalBottles = drinkEntries.reduce((s, [, q]) => s + q, 0);
  const totalNonDrinkItems = nonDrinkEntries.reduce((s, [, q]) => s + q, 0);
  const totalItems = totalBottles + totalNonDrinkItems;

  const subtotal = Object.entries(cart).reduce((s, [id, q]) => s + (products.find(p => p.id === id)?.price || 0) * q, 0);

  // Bulk discount is priced per bottle and applies to drinks only.
  const getDiscount = () => {
    if (totalBottles >= 10) return { amount: totalBottles * 2, tier: 'Premium' };
    // Starter tier needs 5+ bottles of a single drink; merch in the cart is ignored.
    if (totalBottles >= 5 && drinkEntries.length === 1) return { amount: totalBottles * 1, tier: 'Starter' };
    return { amount: 0, tier: null };
  };
  const { amount: discount, tier: discountTier } = getDiscount();
  const calcPromoDiscount = () => {
    if (!promoCode || !promoCodes[promoCode]) return 0;
    const promo = promoCodes[promoCode];
    const afterBulk = subtotal - discount;
    if (afterBulk < promo.minOrder) return 0;
    let amt = promo.type === 'percentage' ? (afterBulk * promo.value) / 100 : Math.min(promo.value, afterBulk);
    if (promo.maxDiscount && amt > promo.maxDiscount) amt = promo.maxDiscount;
    return amt;
  };
  const promoDiscount = calcPromoDiscount();
  const total = Math.max(0, subtotal - discount - promoDiscount);
  const minDate = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const steps = [{ n: 1, l: 'Select' }, { n: 2, l: 'Delivery' }, { n: 3, l: 'Schedule' }, { n: 4, l: 'Contact' }, { n: 5, l: 'Payment' }];

  // Drinks are sold in multiples of 5 (bulk model). Merch and food are sold by
  // the piece — stepping a RM69 tee by 5 would silently put RM345 in the cart.
  const cartStepFor = (id) => (kindOf(id) === 'drink' ? 5 : 1);

  // How many of this product can be in the cart. Drinks are brewed to order,
  // so POS stock doesn't cap them. Merch/food are physical pieces: cap at the
  // POS-maintained `stock` so nobody buys a tee we can't hand over at
  // collection. `stock == null` means the field is missing (pre-stock data or
  // Firestore fallback) — treat as sellable rather than blocking real sales.
  const sellableCap = (id) => {
    if (kindOf(id) === 'drink') return Infinity;
    const stock = products.find(p => p.id === id)?.stock;
    return typeof stock === 'number' ? stock : Infinity;
  };

  const updateCart = (id, delta) => setCart(prev => {
    const nq = Math.min((prev[id] || 0) + delta * cartStepFor(id), sellableCap(id));
    if (nq <= 0) { const { [id]: _, ...r } = prev; return r; }
    return { ...prev, [id]: nq };
  });

  const normalizePromoCode = (input) => {
    if (!input) return '';
    return input.normalize('NFKC').replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/['']/g, "'").replace(/[""]/g, '"').replace(/\s+/g, '').toUpperCase().trim();
  };

  const applyPromo = () => {
    const code = normalizePromoCode(promoInput);
    setPromoError(''); setPromoSuccess('');
    if (!code) { setPromoError('Please enter a promo code'); return; }
    if (!promoCodes[code]) { setPromoError('Invalid promo code. Please check and try again.'); return; }
    const promo = promoCodes[code];
    const currentCartValue = subtotal - discount;
    if (currentCartValue < promo.minOrder) {
      setPromoError(`Minimum order RM${promo.minOrder} required. Your cart: RM${currentCartValue.toFixed(2)}.`);
      return;
    }
    setPromoCode(code); setPromoSuccess(`${promo.description} applied!`); setPromoInput('');
  };
  const removePromo = () => { setPromoCode(''); setPromoInput(''); setPromoError(''); setPromoSuccess(''); };
  const copyText = async (key, text) => {
    if (await BrowserUtils.copyToClipboard(text)) {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(k => (k === key ? '' : k)), 2000);
    }
  };

  // The customer's phone number doubles as the payment reference they type in
  // their bank app — the owners match transfers in the bank account itself.
  const paymentRef = isValidMyMobile(info.phone) ? normalizeMyPhone(info.phone) : '';

  // Fetched as soon as checkout opens, so "Save QR" can hand the file to the
  // share sheet straight from the tap (Safari drops the tap if we fetch first).
  useEffect(() => {
    if (step !== 2 || qrBlobRef.current) return;
    fetch(DUITNOW_QR_URL).then(r => r.blob()).then(b => { qrBlobRef.current = b; }).catch(() => {});
  }, [step]);

  // A QR code on the phone's own screen can't be scanned, so let the customer
  // save it and pick it from their gallery in the bank app's DuitNow QR scanner.
  const saveQr = async () => {
    let blob = qrBlobRef.current;
    if (!blob) {
      try { blob = await (await fetch(DUITNOW_QR_URL)).blob(); } catch (e) { window.open(DUITNOW_QR_URL, '_blank'); return; }
    }
    const file = new File([blob], 'Chamomile-DuitNow-QR.png', { type: blob.type || 'image/png' });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
        return;
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return; // customer closed the share sheet
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = file.name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const checkoutMissing = missingCheckoutFields({ delivery, date, time, name: info.name, phone: info.phone, receiptFile });
  const fieldError = (field) => (showFieldErrors ? checkoutMissing.find(m => m.field === field) : null);
  const inputStyle = (field) => (fieldError(field) ? { ...styles.input, borderColor: colors.error } : styles.input);
  const errorText = (field) => {
    const err = fieldError(field);
    return err ? <p role="alert" style={{ color: colors.error, fontSize: '13px', fontWeight: 500, margin: '8px 0 0' }}>{err.message}</p> : null;
  };

  // Confirm Order is always tappable: if something is missing, say what and
  // take the customer to it instead of leaving a silent grey button.
  const handleConfirmTap = () => {
    if (checkoutMissing.length > 0) {
      setShowFieldErrors(true);
      const el = fieldRefs.current[checkoutMissing[0].field];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el.tagName === 'INPUT' || el.tagName === 'SELECT') el.focus({ preventScroll: true });
      }
      return;
    }
    handleSubmit();
  };

  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (f) {
      if (f.size > 10 * 1024 * 1024) { alert('Max 10MB'); return; }
      setReceiptFile(f);
      try {
        const thumbnail = await ImageUtils.generateThumbnail(f, 200);
        setReceiptPreview(thumbnail);
      } catch (err) {
        const r = new FileReader();
        r.onloadend = () => setReceiptPreview(r.result);
        r.readAsDataURL(f);
      }
    }
  };

  // FIX #5: Enhanced handleSubmit - always show WhatsApp modal for consistent UX
  const handleSubmit = async () => {
    if (!receiptFile) {
      alert('Please upload a payment receipt first.');
      return;
    }
    setSubmitting(true);
    setUploadProgress('Compressing receipt...');

    try {
      // FIXED: Generate order number ONLY when submitting to prevent sequence jumps
      console.log('[Order] Step 1: Getting sequential order number...');
      setUploadProgress('Preparing order number...');
      const nextNum = await orderService.getNextOrderNumber();
      setOrderNumber(nextNum);
      
      console.log('[Order] Step 2: Uploading receipt...');
      const receiptUrl = await uploadReceiptToFirebase(receiptFile, orderId, setUploadProgress);
      setUploadProgress('Saving order...');

      console.log('[Order] Step 3: Building order items...');
      // Belt-and-braces: the stale-cart effect should have pruned unknown ids
      // long before checkout, but this ran unguarded on `p.name` and a miss here
      // throws *after* the order number is spent and the receipt uploaded.
      const items = Object.entries(cart).flatMap(([id, qty]) => {
        const p = products.find(x => x.id === id);
        if (!p) {
          console.warn('[Order] Skipping cart entry with no matching product:', id);
          return [];
        }
        return [{ productId: id, productName: p.name, quantity: qty, unitPrice: p.price, totalPrice: p.price * qty }];
      });
      if (items.length === 0) throw new Error('Your cart is no longer available. Please add items again.');

      console.log('[Order] Step 4: Saving to Firestore...');
      await saveOrderToFirebase({
        orderId, orderNumber: nextNum, items, totalBottles, totalNonDrinkItems, totalItems, subtotal, discount, discountTier,
        promoCode: promoCode || null, promoDiscount, total, deliveryMethod: delivery,
        scheduledDate: date, scheduledTime: time, customerInfo: info,
        paymentReceiptUrl: receiptUrl, status: 'payment_uploaded',
        memberId: memberData?.id || null,
      });

      // Mark promo code as used
      if (promoCode) {
        try { await markDiscountCodeAsUsed(promoCode); } catch (e) { console.warn('[Order] Promo mark failed:', e); }
      }

      try { CookieManager.saveCustomerInfo(info); } catch (e) { console.warn('[Order] Cookie save failed:', e); }
      try { CookieManager.remove('chamomile_cart'); } catch (e) { /* ignore */ }
      setUploadProgress('Sending notification...');

      console.log('[Order] Step 5: Building WhatsApp message...');
      const itemsList = Object.entries(cart).map(([id, q]) => { const p = products.find(x => x.id === id); return `• ${p.name} × ${q} (RM${(p.price * q).toFixed(2)})`; }).join('\n');
      let msg = `🛒 *NEW CHÁMOMILE ORDER!*\n\n📦 *Order:* ${nextNum}\n\n*Items:*\n${itemsList}\n\n💰 *Subtotal:* RM${subtotal.toFixed(2)}`;
      if (discount > 0) msg += `\n🏷️ *Bulk Discount:* -RM${discount.toFixed(2)}`;
      if (promoDiscount > 0) msg += `\n🎫 *Promo (${promoCode}):* -RM${promoDiscount.toFixed(2)}`;
      msg += `\n✨ *Total:* RM${total.toFixed(2)}\n\n👤 ${info.name}\n📞 ${info.phone}`;
      if (info.email) msg += `\n📧 ${info.email}`;
      msg += `\n\n📅 ${date} at ${time}\n🚚 ${delivery}\n\n💳 Receipt uploaded · bank ref: ${paymentRef}\n✅ Please verify and prepare order.`;

      const phone = '60108202187';
      const whatsappUrl = BrowserUtils.getWhatsAppUrl(phone, msg);

      // FIX #5: Always show the modal for consistent UX across all browsers and sessions
      setWhatsAppData({ url: whatsappUrl, message: msg, phone });
      setShowWhatsAppModal(true);

      // Try to open WhatsApp in background (works in most regular browsers)
      try {
        window.open(whatsappUrl, '_blank');
      } catch (e) {
        // Ignore errors - modal is our fallback
      }

      setSubmitting(false);
      setUploadProgress('');
      setDone(true);

      // Award loyalty points if member is logged in
      if (memberData && memberData.id) {
        try {
          const pointsResult = await earnPoints(memberData.id, total, orderId, 'online');
          if (pointsResult.success) {
            console.log(`[Loyalty] Earned ${pointsResult.pointsEarned} pts → ${pointsResult.newBalance} pts`);
            setMemberData(prev => prev ? { ...prev, loyaltyPoints: pointsResult.newBalance } : prev);
          }
        } catch (err) {
          console.error('[Loyalty] Error earning points:', err);
        }
      }
    } catch (error) {
      console.error('[Order] Submit error:', error);
      setUploadProgress('');
      setSubmitting(false);
      alert(`Order submission failed: ${error.message || 'Unknown error'}. Please try again or contact us via WhatsApp.`);
    }
  };

  const reset = () => {
    setStep(0); setCart({}); setDone(false); setDelivery(''); setDate(''); setTime('');
    setInfo({ name: '', phone: '', email: '' }); setReceiptFile(null); setReceiptPreview(null);
    setShowFieldErrors(false); setOrderId(''); setOrderNumber(''); setPromoCode(''); setPromoInput('');
    setPromoError(''); setPromoSuccess('');
  };

  // Old step 0 landing page removed — new Home/Discovery screen is in main render

  if (done) return (
    <div style={{ ...styles.app, paddingBottom: 0 }}>
      <WhatsAppRedirectModal isOpen={showWhatsAppModal} onClose={() => setShowWhatsAppModal(false)} whatsappUrl={whatsAppData.url} message={whatsAppData.message} phone={whatsAppData.phone} colors={colors} />
      <header style={{ textAlign: 'center', padding: '32px 16px', borderBottom: `1px solid ${colors.stone100}` }}><h1 style={styles.logo}>Chámomile</h1></header>
      <div style={{ maxWidth: '400px', margin: '0 auto', padding: '48px 16px', textAlign: 'center' }}>
        {/* Celebration Success Animation */}
        <CelebrationSuccess
          title="Order Submitted!"
          subtitle="Your order has been sent via WhatsApp."
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          style={styles.orderIdBox}
        >
          <p style={{ fontSize: '11px', color: colors.stone500, margin: '0 0 4px', textTransform: 'uppercase' }}>Your Order Number</p>
          <p style={{ fontSize: '32px', fontWeight: 700, color: colors.gold600, margin: 0, fontFamily: 'monospace', letterSpacing: '2px' }}>{orderNumber}</p>
          <p style={{ fontSize: '12px', color: colors.stone500, margin: '8px 0 0' }}>Quote this number when picking up</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          style={styles.card}
        >
          {Object.entries(cart).map(([id, q]) => { const p = products.find(x => x.id === id); return <div key={id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '14px', borderBottom: `1px solid ${colors.stone50}` }}>{p.name} × {q}<span>RM {(p.price * q).toFixed(2)}</span></div>; })}
          {discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '14px', color: colors.success }}>Bulk Discount<span>-RM {discount.toFixed(2)}</span></div>}
          {promoDiscount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '14px', color: colors.success }}>Promo ({promoCode})<span>-RM {promoDiscount.toFixed(2)}</span></div>}

          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '16px', marginTop: '8px', borderTop: `1px solid ${colors.stone200}`, fontWeight: 700 }}>Total<span style={{ color: colors.gold500 }}>RM {total.toFixed(2)}</span></div>
        </motion.div>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.4 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{ ...styles.btnPrimary, marginTop: '32px' }}
          onClick={reset}
        >
          Place Another Order
        </motion.button>
      </div>
    </div>
  );

  return (
    <div style={styles.app}>
      <WhatsAppRedirectModal isOpen={showWhatsAppModal} onClose={() => setShowWhatsAppModal(false)} whatsappUrl={whatsAppData.url} message={whatsAppData.message} phone={whatsAppData.phone} colors={colors} />
      <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
      <PrivacyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <EventInquiryModal isOpen={showEventInquiry} onClose={() => setShowEventInquiry(false)} />

      {selectedProductForReview && (
        <>
          <ReviewSubmitModal
            isOpen={showReviewSubmit}
            onClose={() => { setShowReviewSubmit(false); setSelectedProductForReview(null); }}
            product={selectedProductForReview}
            onSubmitSuccess={async () => {
              const productIds = products.map(p => p.id);
              const ratings = await getAllProductRatings(productIds);
              setProductRatings(ratings);
            }}
          />
          <ProductReviewsModal
            isOpen={showProductReviews}
            onClose={() => { setShowProductReviews(false); setSelectedProductForReview(null); }}
            product={selectedProductForReview}
            onWriteReview={() => { setShowProductReviews(false); setShowReviewSubmit(true); }}
          />
        </>
      )}
      <PrivacyNotice onShowPrivacy={() => setShowPrivacy(true)} />

      {/* Welcome Screen Overlay — first visit only */}
      <AnimatePresence>
        {showWelcome && <WelcomeScreen onDismiss={dismissWelcome} />}
      </AnimatePresence>

      {/* ===== NEW HEADER: Pickup/Delivery location bar (Menu page only) ===== */}
      {step === 0 && (
        <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', background: colors.bg,
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        position: 'sticky', top: 0, zIndex: 50,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%',
            background: colors.stone100, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Icons.MapPin size={16} style={{ color: colors.textPrimary }} />
          </div>
          <div>
            <p style={{ fontSize: '11px', color: colors.textMuted, margin: 0, fontWeight: 500 }}>Pickup at</p>
            <p style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>Forest Hill, KK Sabah</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={handleOpenNotifications}
            style={{
              width: '36px', height: '36px', borderRadius: '50%',
              background: unreadCount > 0 ? colors.gold100 : colors.stone100,
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
            }}
          >
            <Icons.Bell size={18} style={{ color: unreadCount > 0 ? colors.gold600 : colors.textPrimary }} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: '2px', right: '2px',
                background: colors.gold500, color: '#fff',
                fontSize: '9px', fontWeight: 800,
                width: '14px', height: '14px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          {currentUser ? (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: `linear-gradient(135deg, ${colors.gold400}, ${colors.gold500})`,
                  border: 'none', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: '14px', fontWeight: 700,
                  fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(201,169,98,0.3)',
                }}
              >
                {(currentUser.displayName || currentUser.email || '?')[0].toUpperCase()}
              </button>
              {userMenuOpen && (
                <div style={{
                  position: 'absolute', right: 0, top: '44px',
                  background: '#fff', borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  border: `1px solid ${colors.stone100}`,
                  minWidth: '180px', zIndex: 100, overflow: 'hidden',
                }}>
                  <div style={{ padding: '12px 16px', borderBottom: `1px solid ${colors.stone100}` }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: colors.stone800, margin: 0 }}>
                      {currentUser.displayName || 'User'}
                    </p>
                    <p style={{ fontSize: '11px', color: colors.stone400, margin: '2px 0 0' }}>
                      {currentUser.email}
                    </p>
                    {REWARDS_UI_ENABLED && memberData && (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        marginTop: '8px', padding: '6px 10px',
                        background: `linear-gradient(135deg, ${colors.gold50}, ${colors.gold100})`,
                        borderRadius: '8px', border: `1px solid ${colors.gold200}`,
                      }}>
                        <span style={{ fontSize: '12px' }}>{getMemberTier(memberData.loyaltyPoints).icon}</span>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: colors.gold600 }}>
                          {memberData.loyaltyPoints || 0} pts
                        </span>
                        <span style={{ fontSize: '10px', color: colors.stone400 }}>•</span>
                        <span style={{ fontSize: '10px', color: colors.stone500 }}>
                          {getMemberTier(memberData.loyaltyPoints).name}
                        </span>
                      </div>
                    )}
                  </div>
                  {activeOrder && (
                    <MiniOrderTracker order={activeOrder} />
                  )}
                  {memberData && (
                    <button
                      onClick={() => { setShowMemberDashboard(true); setUserMenuOpen(false); }}
                      style={{
                        width: '100%', padding: '12px 16px', background: 'none',
                        border: 'none', borderBottom: `1px solid ${colors.stone100}`,
                        textAlign: 'left', fontSize: '13px',
                        color: colors.gold600, cursor: 'pointer', fontFamily: 'inherit',
                        fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px',
                      }}
                    >
                      👤 My Account
                    </button>
                  )}
                  {memberData && (
                    <button
                      onClick={() => {
                        window.open('https://wa.me/60108202187', '_blank');
                        setUserMenuOpen(false);
                      }}
                      style={{
                        width: '100%', padding: '12px 16px', background: 'none',
                        border: 'none', borderBottom: `1px solid ${colors.stone100}`,
                        textAlign: 'left', fontSize: '13px',
                        color: colors.success, cursor: 'pointer', fontFamily: 'inherit',
                        fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px',
                      }}
                    >
                      <Icons.MessageCircle size={14} style={{ marginRight: '8px', opacity: 0.8 }} />
                      Contact Us (WhatsApp)
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      await logOut();
                      setCurrentUser(null);
                      setMemberData(null);
                      setUserMenuOpen(false);
                    }}
                    style={{
                      width: '100%', padding: '12px 16px', background: 'none',
                      border: 'none', textAlign: 'left', fontSize: '13px',
                      color: colors.error, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowAuthModal(true)}
              style={{
                padding: '8px 16px', borderRadius: '999px',
                background: 'transparent', border: `1.5px solid ${colors.gold400}`,
                color: colors.gold500, fontSize: '12px', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
                transition: 'all 0.2s ease',
              }}
            >
              Sign In
            </button>
          )}
        </div>
      </header>
      )}

      {/* Back Navigation — shown on all screens except Home */}
      {step > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '14px 20px', maxWidth: '500px', margin: '0 auto',
          background: colors.bg,
        }}>
          <button
            onClick={() => { setStep(Math.max(0, step - 1)); if (step === 1) setActiveTab('home'); }}
            style={{
              width: '36px', height: '36px', borderRadius: '50%',
              background: colors.stone100, border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.15s ease',
            }}
          >
            <Icons.ChevronLeft size={18} />
          </button>
          <span style={{ fontSize: '15px', fontWeight: 600, color: colors.textPrimary }}>
            {step === 1 && 'Your Cart'}
            {step === 2 && 'Checkout'}
            {step === 3 && 'Confirmation'}
          </span>
        </div>
      )}

      <div style={styles.container}>
        {/* ===== SCREEN 0: MENU / BROWSE ===== */}
        {step === 0 && (
          <>
            {/* Bold Hero Heading */}
            <div style={{ marginTop: '12px', marginBottom: '12px' }}>
              <h1 style={{
                fontSize: '28px', fontWeight: 800, color: colors.textPrimary,
                margin: 0, lineHeight: 1.2, letterSpacing: '-0.5px',
              }}>
                Thirsty? <span style={{ fontWeight: 400, color: colors.textSecondary }}>Order & Sip.</span>
              </h1>
            </div>

            {/* Search Bar */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '14px',
            }}>
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: '10px',
                padding: '12px 16px', background: '#fff',
                borderRadius: '25px', border: '1px solid rgba(0,0,0,0.08)',
              }}>
                <Icons.Search size={18} style={{ color: colors.textMuted, flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search for oatmilk tea..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    flex: 1, border: 'none', outline: 'none', background: 'transparent',
                    fontSize: '14px', color: colors.textPrimary, fontFamily: 'inherit',
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <Icons.X size={14} style={{ color: colors.textMuted }} />
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowFilterModal(true)}
                style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: colors.black50, border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <circle cx="8" cy="6" r="2" fill="white" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <circle cx="16" cy="12" r="2" fill="white" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                  <circle cx="10" cy="18" r="2" fill="white" />
                </svg>
              </button>
            </div>

            {/* Dynamic Discount Alert — moved to cart page */}

            {/* Private Event entry point — bulk orders stay self-serve below;
                events are negotiated, so this hands off to WhatsApp instead. */}
            <PrivateEventBanner onOpen={() => setShowEventInquiry(true)} />

            {/* Arc-shaped Menu Tabs with Emoji Icons */}
            {(() => {
              // A menu tab ("facet") is either a beverage TAG (Classic, Viral, …)
              // or a non-drink CATEGORY (Merch, Food). Drinks all share the single
              // "Beverage" category, so tags are what separate them for customers.
              //
              // Safety net: a drink with no tag must never become invisible. The
              // POS blocks saving one, but if a product is edited directly in
              // Firestore it falls back to its legacy category, then to a
              // catch-all tab — so it is always reachable by a customer.
              const facetsOf = (p) => {
                if (!isDrinkCategory(p.category)) return [p.category];
                if (Array.isArray(p.tags) && p.tags.length > 0) return p.tags;
                const legacy = (p.category || '').trim();
                return legacy && legacy.toLowerCase() !== 'beverage' ? [legacy] : ['More'];
              };

              // Only show tabs that actually have products behind them.
              const activeFacets = new Set();
              products.forEach(p => facetsOf(p).forEach(f => activeFacets.add(f)));

              // Preferred order: POS-managed tag order first, then POS-managed
              // category order (so Merch lands after the drink tabs), then any
              // stragglers we didn't know about. Deduped because before the data
              // migration the same name can appear in both lists, which would
              // otherwise render the tab twice.
              const preferredOrder = [...new Set([...storeTags, ...storeCategories])];
              const ordered = preferredOrder.filter(f => activeFacets.has(f));
              const remaining = [...activeFacets].filter(f => !preferredOrder.includes(f));
              const tabs = [...ordered, ...remaining];

              // Icons/images are keyed by display label, so tags and categories
              // can share one lookup — tag values win when both define one.
              const facetIcons = { ...categoryIcons, ...tagIcons };
              const facetImages = { ...categoryImages, ...tagImages };

              // Apply search filter — matches product names AND their facets
              // (tags like "Classic"/"Viral", categories like "Merch"), so
              // searching a group name finds the whole group, not nothing.
              const q = searchQuery.trim().toLowerCase();
              const searchFiltered = q
                ? products.filter(p =>
                    p.name.toLowerCase().includes(q) ||
                    facetsOf(p).some(f => f.toLowerCase().includes(q)))
                : products;

              let filteredProducts = selectedCategory === null
                ? searchFiltered
                : searchFiltered.filter(p => facetsOf(p).includes(selectedCategory));

              // Apply Sort
              if (sortBy === 'priceAsc') {
                filteredProducts = filteredProducts.sort((a, b) => a.price - b.price);
              } else if (sortBy === 'priceDesc') {
                filteredProducts = filteredProducts.sort((a, b) => b.price - a.price);
              } else if (sortBy === 'nameAsc') {
                filteredProducts = filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
              }
              // "All" leads the row so there is always a way back to the full
              // menu, and so something is always selected — without it the bar
              // rendered with nothing active on load and picking any tab was a
              // one-way trip. `null` remains the "no filter" state; the tab is
              // just its visible handle. Mirrors the POS product tabs.
              const arcTabs = [
                { label: 'All', value: null },
                ...tabs.filter(t => t !== 'All').map(t => ({ label: t, value: t })),
              ];

              return (
                <>
                  {/* A saved cart pointed at a product that no longer exists. Say so —
                      a total that quietly shrank looks like a pricing bug. */}
                  {staleCartNotice && (
                    <div style={{
                      padding: '10px 14px', borderRadius: '10px', background: colors.roseLight,
                      border: `1px solid ${colors.roseMuted}`, margin: '4px 0 12px',
                      fontSize: '13px', color: colors.espresso, display: 'flex',
                      alignItems: 'center', gap: '8px', position: 'relative', zIndex: 2,
                    }}>
                      <Icons.Alert style={{ color: colors.rose, flexShrink: 0 }} size={14} />
                      <span style={{ flex: 1 }}>Something in your saved cart is no longer on the menu, so we removed it.</span>
                      <button
                        onClick={() => setStaleCartNotice(false)}
                        aria-label="Dismiss"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary, fontSize: '16px', lineHeight: 1, padding: 0 }}
                      >×</button>
                    </div>
                  )}

                  <CategoryArcBar
                    tabs={arcTabs}
                    selected={selectedCategory}
                    onSelect={setSelectedCategory}
                    icons={facetIcons}
                    images={facetImages}
                  />


                  {/* Min bottles warning — inline subtle */}
                  {totalBottles > 0 && totalBottles < 5 && (
                    <div style={{ padding: '10px 14px', borderRadius: '10px', background: colors.gold50, border: `1px solid ${colors.gold200}`, marginBottom: '14px', fontSize: '13px', color: colors.gold700, display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', zIndex: 1 }}>
                      <Icons.Alert style={{ color: colors.gold500, flexShrink: 0 }} size={14} />
                      Add {5 - totalBottles} more bottles to meet minimum order.
                    </div>
                  )}

                  {/* Product Grid — sized products (e.g. Tee S/M/L/XL) collapse into one card.
                      Positioned so the cards sit above the dome's fading tail, which is what
                      lets the two blend instead of meeting at a hard edge. */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px', position: 'relative', zIndex: 1 }}>
                    {groupProductVariants(filteredProducts).map((item, i) => {
                      const isGroup = item.type === 'group';

                      // For a group, the active variant drives price, cart and reviews.
                      const activeId = isGroup
                        ? (selectedVariants[item.baseName] || item.variants[0].id)
                        : item.product.id;
                      const active = isGroup
                        ? (item.variants.find(v => v.id === activeId) || item.variants[0])
                        : item.product;

                      const ratingInfo = productRatings.get(active.id);
                      return (
                        <ProductCard
                          key={isGroup ? `group:${item.baseName}` : active.id}
                          product={active}
                          displayName={isGroup ? item.baseName : null}
                          variants={isGroup ? item.variants : null}
                          cart={isGroup ? cart : null}
                          onSelectVariant={isGroup
                            ? (id) => setSelectedVariants(prev => ({ ...prev, [item.baseName]: id }))
                            : null}
                          quantity={cart[active.id] || 0}
                          onAdd={() => updateCart(active.id, 1)}
                          onRemove={() => updateCart(active.id, -1)}
                          rating={ratingInfo?.averageRating || 0}
                          reviewCount={ratingInfo?.totalReviews || 0}
                          // Merch/food are physical pieces — cap at POS stock.
                          // Drinks are brewed to order and never stock-gated.
                          enforceStock={kindOf(active.id) !== 'drink'}
                          index={i}
                          // The dome sweeps down and outward. Rounding the matching
                          // outer corner of each first-row card carries that curve
                          // into the grid, so the two read as one shape.
                          cornerEcho={i === 0 ? 'left' : i === 1 ? 'right' : null}
                          onFirstAdd={totalItems === 0 ? handleFirstAdd : undefined}
                          onReviewClick={() => {
                            if (ratingInfo?.totalReviews > 0) {
                              setSelectedProductForReview(active);
                              setShowProductReviews(true);
                            }
                          }}
                        />
                      );
                    })}
                  </div>

                  {/* Nothing matched. Previously this rendered an empty grid with no
                      explanation, which reads as a broken page rather than a filter
                      that excluded everything. */}
                  {filteredProducts.length === 0 && (
                    <div style={{
                      textAlign: 'center', padding: '32px 20px 40px',
                      position: 'relative', zIndex: 1,
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '10px' }}>🍃</div>
                      <p style={{ fontSize: '15px', fontWeight: 600, color: colors.textPrimary, margin: '0 0 6px' }}>
                        {searchQuery.trim() ? `No drinks match “${searchQuery.trim()}”` : 'Nothing here yet'}
                      </p>
                      <p style={{ fontSize: '13px', color: colors.textSecondary, margin: '0 0 18px', lineHeight: 1.5 }}>
                        {searchQuery.trim()
                          ? 'Try a different name, or browse the full menu.'
                          : 'This category has no items right now.'}
                      </p>
                      <button
                        onClick={() => { setSearchQuery(''); setSelectedCategory(null); }}
                        style={{
                          padding: '10px 22px', borderRadius: '999px',
                          background: colors.espresso, color: '#FDF6EE',
                          fontSize: '13px', fontWeight: 700, border: 'none',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        Show the full menu
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
            {/* end Menu */}
          </>
        )}

        {/* ===== SCREEN 1: CART ===== */}
        {step === 1 && (
          <>
            {/* Sign-in scarcity banner — guests only */}
            {!currentUser && (
              <div style={{
                background: `linear-gradient(135deg, #FFF3E0, ${colors.gold50})`,
                borderRadius: '14px', padding: '14px 16px', marginBottom: '16px', marginTop: '8px',
                border: `1.5px solid ${colors.gold300}`,
                boxShadow: '0 2px 12px rgba(201,169,98,0.1)',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%',
                    background: colors.gold400, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icons.Alert size={16} style={{ color: '#fff' }} />
                  </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '13px', fontWeight: 700, color: colors.espresso, margin: '0 0 4px' }}>
                        Make ordering easier 🍃
                      </p>
                      <p style={{ fontSize: '11px', color: colors.textSecondary, margin: '0 0 10px', lineHeight: 1.5 }}>
                        Sign in (it takes 10 seconds) to save your details for faster checkout, view your order history, and track your order in real time.
                      </p>
                      <button
                        onClick={() => setShowAuthModal(true)}
                        style={{
                          padding: '8px 18px', borderRadius: '999px',
                          background: colors.espresso, color: '#FDF6EE',
                          fontSize: '12px', fontWeight: 700, border: 'none',
                          cursor: 'pointer', fontFamily: 'inherit',
                          boxShadow: '0 3px 12px rgba(46,26,14,0.2)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Unlock Perks Now
                      </button>
                    </div>
                </div>
              </div>
            )}
            {/* Cart Items */}
            <div style={{ ...styles.card, marginBottom: '16px', marginTop: '8px' }}>
              {Object.entries(cart).length === 0 ? (
                <p style={{ textAlign: 'center', color: colors.textSecondary, padding: '24px 0' }}>Your cart is empty</p>
              ) : (
                Object.entries(cart).map(([id, qty]) => {
                  const p = products.find(x => x.id === id);
                  if (!p) return null;
                  // Merch/food can't step past POS stock (drinks are uncapped).
                  const capReached = qty >= sellableCap(id);
                  return (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: `1px solid ${colors.border}` }}>
                      <div style={{ width: '50px', height: '50px', borderRadius: '10px', overflow: 'hidden', flexShrink: 0, background: '#FFFFFF' }}>
                        <img src={p.imageUrl} alt={p.name} referrerPolicy="no-referrer" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 2px', color: colors.textPrimary }}>{p.name}</p>
                        <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>RM {p.price.toFixed(2)} each</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button onClick={() => updateCart(id, -1)} aria-label={`Remove one ${p.name}`} style={{ ...styles.qtyBtn, background: colors.stone100, color: colors.textPrimary }}>−</button>
                        <span style={{ fontSize: '15px', fontWeight: 700, minWidth: '24px', textAlign: 'center' }}>{qty}</span>
                        <button onClick={() => updateCart(id, 1)} disabled={capReached} aria-label={capReached ? `${p.name}: no more stock available` : `Add one more ${p.name}`} style={{ ...styles.qtyBtn, background: capReached ? colors.stone300 : colors.gold400, color: '#fff', cursor: capReached ? 'not-allowed' : 'pointer' }}>+</button>
                      </div>
                      <p style={{ fontSize: '14px', fontWeight: 700, margin: 0, minWidth: '65px', textAlign: 'right' }}>RM {(p.price * qty).toFixed(2)}</p>
                    </div>
                  );
                })
              )}
            </div>

            {/* Discount Info */}
            {discount > 0 && (
              <div style={{ ...styles.alert, background: colors.successLight, border: '1px solid #bbf7d0', marginBottom: '12px' }}>
                <Icons.Check style={{ color: colors.success, flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: '13px', color: '#166534' }}>
                  <strong>{discountTier} discount applied!</strong> Saving RM {discount.toFixed(2)}
                </p>
              </div>
            )}

            {/* Promo Code */}
            <div style={{ ...styles.card, marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Promo Code</h4>
              {promoCode ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: colors.successLight, borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Icons.Check style={{ color: colors.success }} /><span style={{ fontWeight: 600, color: '#166534' }}>{promoCode}</span><span style={{ color: '#166534', fontSize: '13px' }}>({promoCodes[promoCode]?.description})</span></div>
                  <button onClick={removePromo} style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer' }}><Icons.X size={16} /></button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="text" value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase().replace(/\s/g, ''))} placeholder="Enter promo code" style={{ ...styles.input, flex: 1, textTransform: 'uppercase' }} onKeyPress={(e) => e.key === 'Enter' && applyPromo()} autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck="false" />
                  <button onClick={applyPromo} style={{ ...styles.btnPrimary, width: 'auto', padding: '12px 20px' }}>Apply</button>
                </div>
              )}
              {promoError && <p style={{ color: colors.error, fontSize: '12px', marginTop: '8px' }}>{promoError}</p>}
              {promoSuccess && !promoCode && <p style={{ color: colors.success, fontSize: '12px', marginTop: '8px' }}>{promoSuccess}</p>}
            </div>

            {/* Order Total */}
            <div style={{ ...styles.card, marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '8px' }}><span style={{ color: colors.textSecondary }}>Subtotal ({[
                totalBottles > 0 ? `${totalBottles} bottle${totalBottles !== 1 ? 's' : ''}` : null,
                totalNonDrinkItems > 0 ? `${totalNonDrinkItems} item${totalNonDrinkItems !== 1 ? 's' : ''}` : null,
              ].filter(Boolean).join(' + ')})</span><span>RM {subtotal.toFixed(2)}</span></div>
              {discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: colors.success, marginBottom: '8px' }}><span>Bulk Discount ({discountTier})</span><span>-RM {discount.toFixed(2)}</span></div>}
              {promoDiscount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: colors.success, marginBottom: '8px' }}><span>Promo ({promoCode})</span><span>-RM {promoDiscount.toFixed(2)}</span></div>}
              <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '12px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 700 }}><span>Total</span><span style={{ color: colors.gold600 }}>RM {total.toFixed(2)}</span></div>
            </div>

            <motion.button
              whileTap={{ scale: 0.97 }}
              style={{
                width: '100%', padding: '16px 24px',
                background: colors.black50, color: '#fff',
                border: 'none', borderRadius: '16px',
                fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '8px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                marginBottom: '24px'
              }}
              onClick={() => setStep(2)}
            >
              Checkout · RM{total.toFixed(2)}
            </motion.button>
          </>
        )}

        {/* ===== SCREEN 2: CHECKOUT (merged delivery + schedule + contact + payment) ===== */}
        {step === 2 && (
          <>
            <div style={{ marginTop: '4px' }} />
            {/* Delivery Method */}
            <div ref={el => { fieldRefs.current.delivery = el; }} style={{ ...styles.card, marginBottom: '16px', marginTop: '8px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '14px' }}>Delivery Method</h3>
              <div style={{ ...styles.radioOption(delivery === 'Self-Pickup'), marginBottom: '10px' }} onClick={() => setDelivery('Self-Pickup')}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={styles.radioCircle(delivery === 'Self-Pickup')}>{delivery === 'Self-Pickup' && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }} />}</div>
                  <div style={{ flex: 1 }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>Self-Pickup</span><span style={{ color: colors.success, fontSize: '13px', fontWeight: 500 }}>Free</span></div><p style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '4px' }}>{LOCATION.name}, {LOCATION.address}</p></div>
                </div>
              </div>
              <div style={styles.radioOption(delivery === 'Grab Express')} onClick={() => setDelivery('Grab Express')}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={styles.radioCircle(delivery === 'Grab Express')}>{delivery === 'Grab Express' && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }} />}</div>
                  <div style={{ flex: 1 }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>Grab Express</span><span style={{ color: colors.textSecondary, fontSize: '13px' }}>Customer pays</span></div></div>
                </div>
              </div>
              {errorText('delivery')}
            </div>

            {/* Schedule */}
            <div style={{ ...styles.card, marginBottom: '16px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '14px' }}>Schedule Collection</h3>
              <div style={{ ...styles.alert, ...styles.alertGold, marginBottom: '14px' }}><p style={{ margin: 0, fontSize: '12px' }}>Orders must be placed minimum 1 day in advance</p></div>
              <div ref={el => { fieldRefs.current.date = el; }} style={{ marginBottom: '14px' }}><label style={styles.label}>Collection Date</label><CalendarPicker value={date} onChange={setDate} minDate={minDate} />{errorText('date')}</div>
              <div><label style={styles.label}>Collection Time</label><select ref={el => { fieldRefs.current.time = el; }} value={time} onChange={(e) => setTime(e.target.value)} style={inputStyle('time')}><option value="">Select time</option>{TIMES.map(t => <option key={t} value={t}>{t}</option>)}</select>{errorText('time')}</div>
            </div>

            {/* Contact Information */}
            <div style={{ ...styles.card, marginBottom: '16px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '14px' }}>Contact Information</h3>
              {!currentUser && (
                <div style={{ ...styles.alert, ...styles.alertGold, marginBottom: '14px', cursor: 'pointer' }} onClick={() => setShowAuthModal(true)}>
                  <Icons.User style={{ color: colors.gold500, flexShrink: 0 }} />
                  <div><p style={{ fontWeight: 600, margin: '0 0 2px', fontSize: '13px' }}>Have an account? Sign in to auto-fill</p><p style={{ margin: 0, fontSize: '12px', color: colors.textSecondary }}>Or continue as guest below</p></div>
                </div>
              )}
              {currentUser && (
                <div style={{ ...styles.alert, background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '14px' }}>
                  <Icons.Shield style={{ color: colors.success, flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: '12px', color: colors.success }}>Signed in as <strong>{currentUser.displayName || currentUser.email}</strong></p>
                </div>
              )}
              <div style={{ marginBottom: '14px' }}><label style={styles.label}>Full Name</label><input ref={el => { fieldRefs.current.name = el; }} type="text" autoComplete="name" value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} placeholder="Your full name" style={inputStyle('name')} />{errorText('name')}</div>
              <div style={{ marginBottom: '14px' }}><label style={styles.label}>Phone Number</label><input ref={el => { fieldRefs.current.phone = el; }} type="tel" autoComplete="tel" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} placeholder="e.g. 012-345 6789" style={inputStyle('phone')} />{errorText('phone')}</div>
              <div style={{ marginBottom: '14px' }}><label style={styles.label}>Email <span style={{ fontWeight: 400, textTransform: 'none', color: colors.textMuted }}>(Optional)</span></label><input type="email" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} placeholder="your@email.com" style={styles.input} /></div>
            </div>

            {/* Payment — built to be paid from the same phone the order is placed on */}
            <div style={{ ...styles.card, marginBottom: '16px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '14px' }}>Payment</h3>
              <div style={{ textAlign: 'center', padding: '16px', background: `linear-gradient(135deg, ${colors.gold50}, ${colors.gold100})`, borderRadius: '12px', border: `1px solid ${colors.gold200}`, marginBottom: '16px' }}>
                <p style={{ color: colors.textSecondary, fontSize: '12px', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '1px' }}>Pay</p>
                <p style={{ fontSize: '28px', fontWeight: 700, color: colors.gold600, margin: 0 }}>RM {total.toFixed(2)}</p>
              </div>

              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <img src={DUITNOW_QR_URL} alt="DuitNow QR" style={{ width: '160px', maxWidth: '100%', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <button onClick={saveQr} style={{ ...styles.btnSecondary, width: 'auto', minHeight: '44px', padding: '10px 20px', margin: '12px auto 0', display: 'flex', fontSize: '14px' }}>
                  <Download size={16} aria-hidden="true" /> Save QR
                </button>
                <p style={{ fontSize: '13px', color: colors.textSecondary, margin: '10px 0 0', lineHeight: 1.5 }}>Open your bank app → DuitNow QR → pick from gallery.</p>
                <p style={{ fontSize: '13px', color: colors.textSecondary, margin: '4px 0 0' }}>Shows as: <strong style={{ color: colors.textPrimary }}>{BANK.qrName}</strong></p>
              </div>

              <div style={{ background: colors.stone50, borderRadius: '12px', padding: '14px', fontSize: '14px' }}>
                <p style={{ margin: '0 0 8px', color: colors.textSecondary, fontSize: '13px' }}>Or transfer to {BANK.bank}</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <span style={{ fontWeight: 700, fontSize: '17px', letterSpacing: '0.5px' }}>{BANK.number}</span>
                  <button onClick={() => copyText('account', BANK.number.replace(/\s/g, ''))} aria-label="Copy account number" style={copyBtnStyle}>{copiedKey === 'account' ? 'Copied ✓' : 'Copy'}</button>
                </div>
                <p style={{ fontSize: '13px', color: colors.textSecondary, margin: '6px 0 0' }}>Shows as: <strong style={{ color: colors.textPrimary }}>{BANK.name}</strong></p>

                <div style={{ borderTop: `1px solid ${colors.stone200}`, margin: '14px 0 0', paddingTop: '14px' }}>
                  <p style={{ margin: '0 0 8px', color: colors.textSecondary, fontSize: '13px' }}>Reference: your phone number</p>
                  {paymentRef ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <span style={{ fontWeight: 700, fontSize: '17px', letterSpacing: '0.5px' }}>{paymentRef}</span>
                      <button onClick={() => copyText('reference', paymentRef)} aria-label="Copy payment reference" style={copyBtnStyle}>{copiedKey === 'reference' ? 'Copied ✓' : 'Copy'}</button>
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>Fill in your phone number above, then copy it here.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Receipt Upload + Submit */}
            <div style={{ ...styles.card, marginBottom: '24px' }}>
              <h4 style={{ fontSize: '15px', fontWeight: 700, marginBottom: '12px' }}>Paid? Upload your receipt so we can confirm faster</h4>
              <input type="file" ref={fileInputRef} onChange={handleFile} accept="image/*" style={{ display: 'none' }} />
              <div ref={el => { fieldRefs.current.receipt = el; }} onClick={() => fileInputRef.current?.click()} style={{ border: `2px dashed ${receiptPreview ? colors.success : fieldError('receipt') ? colors.error : colors.border}`, borderRadius: '12px', padding: '20px', textAlign: 'center', cursor: 'pointer', background: receiptPreview ? colors.successLight : 'transparent', transition: 'all 0.3s ease' }}>
                {receiptPreview ? (<div><img src={receiptPreview} alt="Receipt" style={{ maxHeight: '80px', borderRadius: '8px', marginBottom: '8px' }} /><p style={{ color: colors.success, fontWeight: 500, margin: 0, fontSize: '13px' }}>✓ Receipt uploaded</p><p style={{ color: colors.textSecondary, fontSize: '12px', margin: '4px 0 0' }}>Tap to change</p></div>) : (<><div style={{ color: colors.textMuted }}><Icons.Upload /></div><p style={{ fontWeight: 500, marginTop: '8px', fontSize: '14px' }}>Tap to upload receipt</p><p style={{ color: colors.textSecondary, fontSize: '12px', marginTop: '4px' }}>A screenshot from your bank app is fine</p></>)}
              </div>
              {errorText('receipt')}
              <button onClick={handleConfirmTap} disabled={submitting} style={{ ...styles.btnPrimary, ...(submitting ? styles.btnDisabled : styles.btnSuccess), marginTop: '16px', padding: '16px', fontSize: '15px' }}><Icons.Check /> {submitting ? (uploadProgress || 'Processing...') : 'Confirm Order'}</button>
              {showFieldErrors && checkoutMissing.length > 0 && (
                <p role="status" style={{ textAlign: 'center', color: colors.error, fontSize: '13px', marginTop: '8px' }}>
                  {checkoutMissing.length === 1 ? '1 thing' : `${checkoutMissing.length} things`} still needed — marked in red above.
                </p>
              )}
              <p style={{ textAlign: 'center', fontSize: '12px', color: colors.textSecondary, margin: '12px 0 0', lineHeight: 1.5 }}>
                By confirming, you agree to our <button onClick={() => setShowTerms(true)} style={linkBtnStyle}>Terms</button> and <button onClick={() => setShowPrivacy(true)} style={linkBtnStyle}>Privacy Policy</button>.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}><div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: colors.stone100, borderRadius: '999px' }}><Icons.Lock size={14} /><span style={{ fontSize: '11px', fontWeight: 500, color: colors.textSecondary }}>100% Secure Checkout</span></div></div>
            </div>
          </>
        )}
      </div>

      {/* ===== NEW 3-TAB BOTTOM NAVIGATION BAR ===== */}
      {step < 2 && (
        <>
          {/* Cart screen: Checkout CTA has been moved to order summary */}

          {/* Bottom Tab Bar — floating island with blur */}
          {step === 0 && (
            <div style={{
              position: 'fixed', bottom: '16px', left: '50%', transform: 'translateX(-50%)',
              zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', padding: '8px 24px',
              background: 'rgba(28,25,23,0.96)', backdropFilter: 'blur(20px)', // Dark theme background
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '999px',
              boxShadow: '0 8px 32px 8px rgba(255,255,255,0.8), 0 0 20px 4px rgba(255,255,255,0.6), inset 0 0 0 1px rgba(255,255,255,1)',
            }}>
            {/* Menu Tab */}
            <button
              onClick={() => { setActiveTab('home'); setStep(0); }}
              style={{
                width: '44px', height: '44px', borderRadius: '50%',
                background: activeTab === 'home' ? colors.gold400 : 'transparent',
                border: 'none', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              <Icons.Menu size={20} style={{ color: activeTab === 'home' ? '#fff' : colors.stone400 }} />
            </button>

            {/* Cart Tab */}
            <button
              onClick={() => { setActiveTab('cart'); if (totalItems > 0) setStep(1); }}
              style={{
                width: '44px', height: '44px', borderRadius: '50%',
                background: activeTab === 'cart' ? colors.gold400 : 'transparent',
                border: 'none', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                position: 'relative', transition: 'all 0.2s ease',
              }}
            >
              <Icons.ShoppingCart size={20} style={{ color: activeTab === 'cart' ? '#fff' : colors.stone400 }} />
              {totalItems > 0 && (
                <span style={{
                  position: 'absolute', top: '2px', right: '2px',
                  background: '#fff', color: colors.black50,
                  fontSize: '10px', fontWeight: 800,
                  width: '18px', height: '18px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                }}>
                  {totalItems}
                </span>
              )}
            </button>

            {/* Profile Tab */}
            <button
              onClick={() => {
                setActiveTab('profile');
                if (currentUser) {
                  setUserMenuOpen(true);
                } else {
                  setShowAuthModal(true);
                }
              }}
              style={{
                width: '44px', height: '44px', borderRadius: '50%',
                background: activeTab === 'profile' ? colors.gold400 : 'transparent',
                border: 'none', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              <Icons.User size={20} style={{ color: activeTab === 'profile' ? '#fff' : colors.stone400 }} />
            </button>
          </div>
          )}
        </>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={(user) => setCurrentUser(user)}
        onShowTerms={() => { setShowAuthModal(false); setShowTerms(true); }}
        onShowPrivacy={() => { setShowAuthModal(false); setShowPrivacy(true); }}
      />

      {/* Member Dashboard Modal */}
      {
        showMemberDashboard && memberData && (
          <MemberDashboard
            memberData={memberData}
            currentUser={currentUser}
            onClose={() => setShowMemberDashboard(false)}
          />
        )
      }

      {/* Notifications Modal Overlay */}
      <AnimatePresence>
        {showNotifications && (() => {
          const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
          // Determine qualifying tiers for current user
          const tierName = memberData ? (getMemberTier(memberData.loyaltyPoints || 0).name || '').toLowerCase() : null;

          // Each tier value and who can see it:
          // 'all' → everyone
          // 'non-members' → guests only
          // 'tea leaf' / 'blossom' / 'royal blend' / 'grand cru' → cumulative (this tier and above)
          // '<tier> only' → exclusive to that single tier
          const qualifyingTiers = !tierName
            ? ['all', 'non-members']
            : tierName === 'tea leaf'
              ? ['all', 'tea leaf', 'tea leaf only']
              : tierName === 'blossom'
                ? ['all', 'tea leaf', 'blossom', 'blossom only']
                : tierName === 'royal blend'
                  ? ['all', 'tea leaf', 'blossom', 'royal blend', 'royal blend only']
                  : ['all', 'tea leaf', 'blossom', 'royal blend', 'grand cru', 'grand cru only'];

          const visibleNotifs = appNotifications.filter(n => {
            if (dismissedNotifs.has(n.id)) return false;
            const ts = n.createdAt?.toDate?.();
            if (ts && Date.now() - ts.getTime() > THIRTY_DAYS) return false; // auto-expire
            return qualifyingTiers.includes((n.targetTier || 'all').toLowerCase());
          });

          return (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300 }}
              onClick={() => setShowNotifications(false)}
            >
              <motion.div
                initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                style={{
                  position: 'fixed', top: 0, right: 0, bottom: 0, width: '85%', maxWidth: '380px',
                  background: '#fff', boxShadow: '-8px 0 24px rgba(0,0,0,0.1)',
                  display: 'flex', flexDirection: 'column',
                }}
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div style={{ padding: '20px', borderBottom: `1px solid ${colors.stone100}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Announcements</h3>
                    {visibleNotifs.length > 0 && (
                      <span style={{ background: colors.gold100, color: colors.gold700, fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px' }}>
                        {visibleNotifs.length}
                      </span>
                    )}
                  </div>
                  <button onClick={() => setShowNotifications(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Icons.X size={20} style={{ color: colors.stone500 }} /></button>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                  {visibleNotifs.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: colors.stone400, paddingBottom: '60px' }}>
                      <Icons.Bell size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                      <p style={{ margin: 0, fontWeight: 500, color: colors.stone600 }}>No announcements yet</p>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', textAlign: 'center' }}>We&apos;ll post updates, promotions, and news here.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {!currentUser && (
                        <div style={{
                          padding: '12px 14px', borderRadius: '12px',
                          background: colors.gold50, border: `1px solid ${colors.gold200}`,
                          display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px',
                        }}>
                          <span style={{ fontSize: '16px' }}>🔒</span>
                          <div>
                            <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: colors.gold700 }}>Sign in to unlock tier-exclusive announcements</p>
                            <button
                              onClick={() => { setShowNotifications(false); setShowAuthModal(true); }}
                              style={{ background: 'none', border: 'none', padding: 0, fontSize: '12px', color: colors.gold600, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit', marginTop: '4px' }}
                            >Sign In / Register</button>
                          </div>
                        </div>
                      )}
                      {visibleNotifs.map(n => {
                        const ts = n.createdAt?.toDate?.() || null;
                        return (
                          <div key={n.id} style={{
                            padding: '14px 16px', borderRadius: '14px',
                            background: `linear-gradient(135deg, ${colors.gold50}, #fef3c7)`,
                            border: `1px solid ${colors.gold200}`,
                            boxShadow: '0 2px 8px rgba(201,169,98,0.08)',
                            position: 'relative',
                          }}>
                            <button
                              onClick={() => dismissNotification(n.id)}
                              style={{
                                position: 'absolute', top: '10px', right: '10px',
                                width: '20px', height: '20px', borderRadius: '50%',
                                background: 'rgba(0,0,0,0.06)', border: 'none',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                              title="Dismiss"
                            >
                              <Icons.X size={10} style={{ color: colors.stone400 }} />
                            </button>
                            <div style={{ paddingRight: '24px', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: colors.stone800 }}>{n.title}</p>
                                {ts && (
                                  <span style={{ fontSize: '10px', color: colors.stone400, whiteSpace: 'nowrap', flexShrink: 0, marginTop: '2px' }}>
                                    {ts.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}
                                  </span>
                                )}
                              </div>
                            </div>
                            <p style={{ margin: 0, fontSize: '13px', color: colors.stone600, lineHeight: 1.6 }}>{n.message}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Filter Modal Overlay */}
      <AnimatePresence>
        {showFilterModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
            onClick={() => setShowFilterModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              style={{
                background: '#fff', width: '100%', maxWidth: '500px',
                borderRadius: '24px 24px 0 0', padding: '24px 24px 40px',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.1)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Sort & Filter</h3>
                <button onClick={() => setShowFilterModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Icons.X size={20} color={colors.stone500} /></button>
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: colors.stone500, marginBottom: '12px' }}>Sort By</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { id: 'featured', label: 'Featured' },
                    { id: 'priceAsc', label: 'Price: Low to High' },
                    { id: 'priceDesc', label: 'Price: High to Low' },
                    { id: 'nameAsc', label: 'Name: A to Z' }
                  ].map(option => (
                    <label key={option.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '12px', background: sortBy === option.id ? colors.gold50 : colors.stone50, borderRadius: '12px', border: `1px solid ${sortBy === option.id ? colors.gold400 : 'transparent'}` }}>
                      <span style={{ fontSize: '15px', fontWeight: sortBy === option.id ? 600 : 500, color: sortBy === option.id ? colors.gold700 : colors.stone800 }}>{option.label}</span>
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: `2px solid ${sortBy === option.id ? colors.gold500 : colors.stone300}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {sortBy === option.id && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: colors.gold500 }} />}
                      </div>
                      <input type="radio" checked={sortBy === option.id} onChange={() => setSortBy(option.id)} style={{ display: 'none' }} />
                    </label>
                  ))}
                </div>
              </div>

              <button 
                onClick={() => setShowFilterModal(false)}
                style={{ ...styles.btnPrimary, background: colors.black50, fontSize: '16px', padding: '16px' }}
              >
                Apply
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div >
  );
}

export { AuthModal };
